import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, aiApi, extractApiError } from '../api/client';
import { usePipelineStore, Pipeline } from '../store/pipelineStore';
import { useCreditsStore, CREDIT_COSTS } from '../store/creditsStore';
import LoadingCinematic, { useLoadingSteps } from '../components/LoadingCinematic';
import { CourseSnippet } from '../components/CourseSnippet';
import { LogoLockup } from '../components/LogoLockup';
import GoDeeper from '../components/PowerModeToggle';
import InsufficientCreditsModal from '../components/InsufficientCreditsModal';
import ReviewModal from '../components/ReviewModal';
import { getScoreLabel, SCORE_DESCRIPTIONS } from '../lib/scoreLabels';
import { useSubscription } from '../hooks/useSubscription';

// ── Shared API error classifier ───────────────────────────────
// Returns a human-readable message for timeout / network / server / validation errors.
// Use this in every catch block instead of extractApiError(err).message so users
// never see the generic "An unexpected error occurred" fallback.
function classifyApiError(err: unknown): string {
  const e = err as {
    code?: string;
    message?: string;
    response?: { status: number; data?: { error?: { message: string } } };
  };
  if (e.code === 'ECONNABORTED' || e.message?.includes('timeout')) {
    // FIX: #2 — timeout message reflects 90s limit
    return 'The request timed out. AI analysis can take up to 90 seconds — please try again.';
  }
  if (!e.response) {
    return 'Could not reach the server. Please check your connection and try again.';
  }
  if (e.response.status === 402) {
    return 'insufficient_credits';
  }
  if (e.response.status >= 500) {
    return e.response.data?.error?.message ?? `Server error (${e.response.status}). Please try again.`;
  }
  // 4xx or structured API error — extractApiError handles these correctly
  return extractApiError(err).message;
}

// ── Design tokens (mirrors CSS vars) ─────────────────────────
const C = {
  bg: '#050510',
  surface: '#0d0d1f',
  surface2: '#13132a',
  border: 'rgba(255,255,255,0.06)',
  borderHover: 'rgba(124,111,247,0.4)',
  primary: '#7c6ff7',
  primaryGlow: 'rgba(124,111,247,0.3)',
  orange: '#f97316',         // CTA buttons — matches app-wide btn-primary
  orangeGlow: 'rgba(249,115,22,0.35)',
  accent: '#00d4ff',
  green: '#00e676',
  amber: '#ffb800',
  danger: '#ff4466',
  text: '#f0f0ff',
  text2: '#8888aa',
  text3: '#444466',
};

// ── Shared helpers ─────────────────────────────────────────────

/** Safely convert any AI output value to a renderable string.
 *  Prevents React error #31 ("Objects are not valid as a React child"). */
function S(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(S).join(', ');
  try { return JSON.stringify(v); } catch { return ''; }
}

/** Safely convert unknown value to string array */
function SA(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map(item => S(item));
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { copyToClipboard(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      style={{
        background: 'transparent',
        border: `1px solid ${copied ? C.green : C.border}`,
        borderRadius: 6,
        color: copied ? C.green : C.text2,
        fontSize: 11,
        padding: '3px 8px',
        cursor: 'pointer',
        transition: 'all 0.2s',
      }}
    >
      {copied ? '✓ Copied' : '⎘ Copy'}
    </button>
  );
}

// ── Animated score hook (IntersectionObserver + count-up) ─────
function useAnimatedScore(targetValue: number, duration = 1200) {
  const [value, setValue] = useState(0);
  const [labelVisible, setLabelVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current || hasAnimated) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const start = performance.now();
          const animate = (now: number) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            // ease-out: 1 - (1-t)^3
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(Math.round(eased * targetValue));
            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setValue(targetValue);
              setTimeout(() => setLabelVisible(true), 300);
            }
          };
          requestAnimationFrame(animate);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [targetValue, duration, hasAnimated]);

  return { ref, value, labelVisible };
}

function ScoreBar({ label, score, color, animated }: { label: string; score: number; color: string; animated?: boolean }) {
  const scoreInfo = getScoreLabel(score);
  const desc = SCORE_DESCRIPTIONS[label];
  const anim = useAnimatedScore(score);
  const displayScore = animated ? anim.value : score;
  const barWidth = animated ? anim.value : score;

  return (
    <div ref={animated ? anim.ref : undefined} style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
        <span style={{ color: C.text2 }} title={desc}>{label}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color, fontWeight: 700 }}>{displayScore}</span>
          {(!animated || anim.labelVisible) && (
            <span style={{ fontSize: 11, color: scoreInfo.color, opacity: animated ? 0 : 1, animation: animated && anim.labelVisible ? 'fadeIn 0.3s ease forwards' : undefined }}>{scoreInfo.emoji} {scoreInfo.text}</span>
          )}
        </span>
      </div>
      <div style={{ background: C.surface2, borderRadius: 4, height: 6, overflow: 'hidden' }}>
        <div style={{
          width: `${barWidth}%`, height: '100%', background: color, borderRadius: 4,
          transition: animated ? 'none' : undefined,
          animation: animated ? undefined : 'bar-fill 1s ease 0.3s both',
        }} />
      </div>
      {desc && <p style={{ fontSize: 11, color: C.text3, marginTop: 3 }}>{desc}</p>}
    </div>
  );
}

function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 18,
      padding: 28,
      ...style,
    }}>
      {children}
    </div>
  );
}

function GradientBtn({
  onClick, disabled, children, style,
}: {
  onClick: () => void; disabled?: boolean; children: React.ReactNode; style?: React.CSSProperties;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        background: disabled ? C.surface2 : C.orange,
        border: 'none',
        borderRadius: 14,
        color: '#fff',
        fontSize: 17,
        fontWeight: 700,
        padding: '16px 0',
        height: 56,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background 0.2s, box-shadow 0.2s',
        boxShadow: disabled ? 'none' : `0 4px 18px ${C.orangeGlow}`,
        letterSpacing: 0.3,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

const STEP_COST_KEYS = ['PIPELINE_PROFILE', 'PIPELINE_IDEA', 'PIPELINE_REALITY', 'PIPELINE_POSITIONING', 'PIPELINE_VALIDATION'] as const;

function CreditCostLabel({ step, isFirst }: { step: number; isFirst?: boolean }) {
  const { isSubscribed } = useSubscription();
  const key = STEP_COST_KEYS[step];
  if (!key) return null;
  const cost = CREDIT_COSTS[key];
  if (isFirst) return <span style={{ fontSize: 12, color: '#8888aa', fontWeight: 400, marginLeft: 6 }}>(free)</span>;
  if (isSubscribed) return <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 400, marginLeft: 6 }}>Included in Pro ✓</span>;
  return <span style={{ fontSize: '0.75rem', color: '#f97316', fontWeight: 400, marginLeft: 6, opacity: 0.7 }}>  ·  ⚡ {cost} cr</span>;
}

function GhostBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        background: 'transparent',
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        color: C.text2,
        fontSize: 14,
        padding: '14px 0',
        cursor: 'pointer',
        transition: 'border-color 0.2s',
      }}
    >
      {children}
    </button>
  );
}

// FIX: #7 — "Retry this step" as a real clickable button with credits-restored notice
function ErrMsg({ msg, onDismiss, onRetry }: { msg: string; onDismiss?: () => void; onRetry?: () => void }) {
  if (!msg) return null;
  return (
    <div style={{
      background: `${C.danger}12`, border: `1px solid ${C.danger}40`,
      borderRadius: 10, padding: '12px 16px', marginBottom: 16,
      color: C.danger, fontSize: 13,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1 }}><strong>Error:</strong> {msg}</span>
        {onRetry && (
          <button onClick={onRetry} style={{
            background: `${C.danger}20`, border: `1px solid ${C.danger}40`, borderRadius: 8,
            color: C.danger, cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: '8px 16px', whiteSpace: 'nowrap',
          }}>↺ Retry this step</button>
        )}
        {onDismiss && (
          <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: C.danger, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>✕</button>
        )}
      </div>
      {onRetry && (
        <span style={{ fontSize: 11, color: '#6b7280', marginTop: 6, display: 'block' }}>Credits were restored</span>
      )}
    </div>
  );
}

// ── useContinue: safe step-advance hook ───────────────────────
// Fetches the fresh pipeline then calls onDone; surfaces errors in UI.
function useContinue(pipelineId: string, onDone: (p: Pipeline) => void) {
  const [advancing, setAdvancing] = useState(false);
  const [advanceError, setAdvanceError] = useState('');

  const advance = async (validate?: (p: Pipeline) => string | null) => {
    setAdvancing(true);
    setAdvanceError('');
    try {
      console.log('[Pipeline] Fetching updated pipeline:', pipelineId);
      const { data } = await api.get(`/pipeline/${pipelineId}`);
      console.log('[Pipeline] Server response:', data);
      const updated: Pipeline = data?.data?.pipeline;
      if (!updated?.id) {
        throw new Error('Server returned invalid pipeline data — missing id');
      }
      if (validate) {
        const msg = validate(updated);
        if (msg) throw new Error(msg);
      }
      console.log('[Pipeline] Advancing to step', updated.currentStep);
      onDone(updated);
    } catch (err) {
      console.error('[Pipeline] Advance error:', err);
      setAdvanceError(classifyApiError(err));
      setAdvancing(false);
    }
  };

  return { advancing, advanceError, clearAdvanceError: () => setAdvanceError(''), advance };
}

// ── Helpers ────────────────────────────────────────────────────

function inferCategoryFromIdea(pipeline: Pipeline): string {
  const text = [
    pipeline.idea?.rawIdea ?? '',
    S(pipeline.idea?.aiOutput?.ideaSnapshot) ?? '',
  ].join(' ').toLowerCase();
  if (text.includes('marketplace') || text.includes('platform')) return 'Marketplace';
  if (text.includes('enterprise') || text.includes('b2b')) return 'B2B SMB';
  if (text.includes('consumer') || text.includes('b2c')) return 'B2C';
  if (text.includes('saas') || text.includes('software') || text.includes('app')) return 'SaaS';
  return 'B2B SMB';
}

function deriveTargetSegment(text: string): string {
  const t = text.toLowerCase();
  if (t.includes('enterprise')) return 'B2B Enterprise';
  if (t.includes('marketplace') || t.includes('platform')) return 'Marketplace';
  if (t.includes('b2c') || t.includes('consumer')) return 'B2C';
  if (t.includes('saas') || t.includes('software')) return 'SaaS';
  if (t.includes('small business') || t.includes('smb') || t.includes('freelan')) return 'B2B SMB';
  return 'B2B SMB';
}

// ── Read-only step view for back-navigation ─────────────────
const STEP_OUTPUT_KEYS = ['userProfile', 'idea', 'realityCheck', 'positioning', 'validationPath'] as const;

function ReadOnlyStepView({ pipeline, stepIndex, onBackToCurrent, onRerun }: {
  pipeline: Pipeline; stepIndex: number;
  onBackToCurrent?: () => void;
  onRerun?: (stepIndex: number) => void;
}) {
  const [showRerunConfirm, setShowRerunConfirm] = useState(false);
  const stepKey = STEP_OUTPUT_KEYS[stepIndex];
  const stepData = stepKey ? (pipeline[stepKey] as Record<string, unknown> | undefined) : null;
  const aiOutput = (stepData?.aiOutput ?? {}) as Record<string, unknown>;
  const costKey = STEP_COST_KEYS[stepIndex];
  const cost = costKey ? CREDIT_COSTS[costKey] : 0;

  if (!aiOutput || Object.keys(aiOutput).length === 0) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center', padding: '40px 0' }}>
        <p style={{ color: C.text2, fontSize: 14 }}>No saved output for this step.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', animation: 'fadeUp 0.3s ease' }}>
      <div style={{
        background: 'rgba(124,111,247,0.06)', border: '1px solid rgba(124,111,247,0.2)',
        borderRadius: 12, padding: '10px 16px', marginBottom: 20,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontSize: 13, color: '#8888aa',
      }}>
        <span>📋 Viewing saved results for {STEP_LABELS[stepIndex]} — read only</span>
        <span style={{
          background: 'rgba(0,230,118,0.12)', color: '#00e676',
          borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700,
        }}>Completed</span>
      </div>

      {Object.entries(aiOutput).map(([key, value]) => {
        if (value === null || value === undefined) return null;
        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
        return (
          <div key={key} style={{
            background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 12, padding: '14px 18px', marginBottom: 10,
          }}>
            <h4 style={{ fontSize: 12, fontWeight: 700, color: C.primary, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {label}
            </h4>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {typeof value === 'string' ? value
                : typeof value === 'number' ? String(value)
                : Array.isArray(value) ? value.map((v, i) => (
                    <div key={i} style={{ marginBottom: 4 }}>
                      {typeof v === 'string' ? `• ${v}` : typeof v === 'object' && v ? JSON.stringify(v, null, 2) : String(v)}
                    </div>
                  ))
                : typeof value === 'object' ? (
                    <pre style={{ fontSize: 12, color: C.text2, overflow: 'auto', margin: 0 }}>
                      {JSON.stringify(value, null, 2)}
                    </pre>
                  )
                : String(value)}
            </div>
          </div>
        );
      })}

      {/* Bottom actions */}
      <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
        {onBackToCurrent && (
          <button
            onClick={onBackToCurrent}
            style={{
              flex: 1, padding: '12px 0', borderRadius: 12,
              background: 'transparent', border: `1px solid ${C.border}`,
              color: C.text2, fontSize: 14, cursor: 'pointer',
            }}
          >
            ← Back to current step
          </button>
        )}
        {onRerun && (
          <button
            onClick={() => setShowRerunConfirm(true)}
            style={{
              flex: 1, padding: '12px 0', borderRadius: 12,
              background: 'rgba(124,111,247,0.08)', border: `1px solid rgba(124,111,247,0.3)`,
              color: C.primary, fontSize: 14, cursor: 'pointer',
            }}
          >
            🔄 Re-run this step  ·  ⚡ {cost} cr
          </button>
        )}
      </div>

      {/* Re-run confirmation */}
      {showRerunConfirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setShowRerunConfirm(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#0d0d1f', border: `1px solid ${C.border}`,
            borderRadius: 18, padding: '28px 32px', maxWidth: 400, textAlign: 'center',
          }}>
            <p style={{ color: '#fff', fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
              Re-run {STEP_LABELS[stepIndex]}?
            </p>
            <p style={{ color: C.text2, fontSize: 13, marginBottom: 20 }}>
              This will cost {cost} credits and replace your current results.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowRerunConfirm(false)} style={{
                flex: 1, padding: '10px', borderRadius: 10, background: 'transparent',
                border: `1px solid ${C.border}`, color: C.text2, cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={() => { setShowRerunConfirm(false); onRerun!(stepIndex); }} style={{
                flex: 1, padding: '10px', borderRadius: 10, background: C.orange,
                border: 'none', color: '#fff', fontWeight: 700, cursor: 'pointer',
              }}>Continue</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function extractPriceFromBlueprint(aiOutput: Record<string, unknown>): number {
  try {
    const m = JSON.stringify(aiOutput.successMetrics ?? '').match(/\$(\d+)/);
    if (m) return parseInt(m[1], 10);
  } catch { /* ignore */ }
  return 29;
}

// ── Launch Momentum Screen ──────────────────────────────────────

function LaunchMomentumScreen({ pipeline, onDismiss }: { pipeline: Pipeline; onDismiss: () => void }) {
  const navigate = useNavigate();
  const [linkCopied, setLinkCopied] = useState(false);
  const [redirectIn, setRedirectIn] = useState(15);
  const [redirectPaused, setRedirectPaused] = useState(false);

  // Gentle auto-redirect with visible countdown — pauses on scroll/interaction
  useEffect(() => {
    if (redirectPaused || !pipeline.linkedStartupId || redirectIn <= 0) return;
    const timer = setTimeout(() => {
      if (redirectIn === 1) {
        navigate(`/startups/${pipeline.linkedStartupId}/dashboard`);
      }
      setRedirectIn(prev => prev - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [redirectIn, redirectPaused, pipeline.linkedStartupId, navigate]);

  // Extract product name vs full description
  const posAI = pipeline.positioning?.aiOutput ?? {};
  const ideaAI = pipeline.idea?.aiOutput ?? {};
  const productName = String(posAI.suggestedProductName ?? '').trim() ||
    String(ideaAI.ideaSnapshot ?? '').split(/[.,:—–]/)[0].trim().slice(0, 40) ||
    (pipeline.idea?.rawIdea ?? 'Your Idea').slice(0, 40);
  const ideaSubtitle = String(ideaAI.ideaSnapshot ?? pipeline.idea?.rawIdea ?? '');

  // Fix 6: Set document title
  useEffect(() => {
    document.title = `${productName} — StartupScore`;
  }, [productName]);

  // Validation score from reality check
  const validationScore =
    (pipeline.realityCheck?.aiOutput?.validationScore as number) ??
    (pipeline.realityCheck?.aiOutput?.overallScore as number) ??
    null;

  // Payment/validation results analysis
  const paymentAI = (pipeline.paymentTest?.aiOutput ?? {}) as Record<string, unknown>;
  const paymentScore = (paymentAI.paymentValidationScore as number) ?? null;
  const userResults = pipeline.validationPath?.userResults;
  const hasValidationResults = !!userResults?.submittedAt;

  // Determine BUILD / PIVOT / KILL verdict
  const effectiveScore = paymentScore ?? validationScore ?? 0;
  const paidConversions = userResults?.conversions ?? 0;
  const preOrders = userResults?.preOrders ?? 0;

  let verdict: 'BUILD' | 'PIVOT' | 'KILL' = 'PIVOT';
  let verdictEmoji = '🔄';
  let verdictColor = C.amber;
  if (effectiveScore >= 60 || paidConversions >= 3 || preOrders >= 2) {
    verdict = 'BUILD';
    verdictEmoji = '🚀';
    verdictColor = C.green;
  } else if (effectiveScore < 30 && paidConversions === 0 && preOrders === 0) {
    verdict = 'KILL';
    verdictEmoji = '💀';
    verdictColor = C.danger;
  }

  // AI verdict reasoning
  const aiVerdict = String(paymentAI.verdict ?? '');
  if (aiVerdict.toLowerCase().includes('kill') || aiVerdict.toLowerCase() === 'kill') {
    verdict = 'KILL'; verdictEmoji = '💀'; verdictColor = C.danger;
  } else if (aiVerdict.toLowerCase().includes('continue') || aiVerdict.toLowerCase() === 'continue') {
    verdict = 'BUILD'; verdictEmoji = '🚀'; verdictColor = C.green;
  }

  // Score breakdown from reality check
  const scores = (pipeline.realityCheck?.aiOutput?.scores as Record<string, number>) ?? {};
  const scoreEntries = Object.entries(scores);

  // AI insights from validation results analysis
  const whatWorks = SA(paymentAI.whatWorks) ?? [];
  const whatToAdjust = SA(paymentAI.whatToAdjust) ?? [];
  const encouragingInsights = SA(paymentAI.encouragingInsights) ?? [];
  const warningSignals = SA(paymentAI.warningSignals) ?? [];
  const verdictReasoning = String(paymentAI.verdictReasoning ?? paymentAI.dataInterpretation ?? '');
  const nextExperiment = String(paymentAI.nextExperiment ?? '');
  const fullAnalysis = String(paymentAI.fullAnalysis ?? '');

  // Build data insights from user results
  const dataInsights: string[] = [];
  if (hasValidationResults) {
    if ((userResults?.visits ?? 0) > 0) dataInsights.push(`${userResults!.visits} landing page visits recorded`);
    if (paidConversions > 0) dataInsights.push(`${paidConversions} conversions — people took action on your offer`);
    if ((userResults?.emailsCollected ?? 0) > 0) dataInsights.push(`${userResults!.emailsCollected} emails collected — interest confirmed`);
    if (preOrders > 0) dataInsights.push(`${preOrders} pre-orders — strongest signal of demand`);
    if ((userResults?.visits ?? 0) > 0 && paidConversions > 0) {
      const rate = ((paidConversions / userResults!.visits!) * 100).toFixed(1);
      dataInsights.push(`${rate}% conversion rate`);
    }
  }
  // Add AI-generated insights
  encouragingInsights.forEach(i => dataInsights.push(i));

  const handleCopyLink = () => {
    const url = `${window.location.origin}/results/${pipeline.id}`;
    navigator.clipboard.writeText(url).catch(() => {});
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <div
      onScroll={() => setRedirectPaused(true)}
      style={{
      position: 'fixed', inset: 0, background: C.bg, zIndex: 200,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '40px 24px', overflowY: 'auto', animation: 'fadeUp 0.4s ease',
    }}>
      <div style={{ maxWidth: 680, width: '100%', textAlign: 'center' }}>
        <Confetti active />

        {/* Verdict Hero */}
        <div style={{ marginBottom: 12, fontSize: 64, animation: 'float 3s ease-in-out infinite' }}>{verdictEmoji}</div>
        <div style={{ color: C.primary, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 8 }}>
          Pipeline Complete
        </div>
        <h1 style={{ fontSize: 32, fontWeight: 900, color: C.text, marginBottom: 4, lineHeight: 1.2 }}>
          {productName}
        </h1>
        {ideaSubtitle && ideaSubtitle !== productName && (
          <p style={{ color: C.text2, fontSize: 14, lineHeight: 1.5, marginBottom: 16, maxWidth: 500, margin: '0 auto 16px' }}>
            {ideaSubtitle.length > 120 ? ideaSubtitle.slice(0, 120) + '...' : ideaSubtitle}
          </p>
        )}

        {/* THE VERDICT */}
        <div style={{
          background: `${verdictColor}12`,
          border: `2px solid ${verdictColor}50`,
          borderRadius: 20,
          padding: '28px 32px',
          marginBottom: 28,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: verdictColor, marginBottom: 8 }}>
            The Verdict
          </div>
          <div style={{ fontSize: 36, fontWeight: 900, color: verdictColor, marginBottom: 8, animation: 'verdict-stamp 0.6s ease both' }}>
            {verdict === 'BUILD' ? 'BUILD IT' : verdict === 'PIVOT' ? 'PIVOT' : 'KILL IT'}
          </div>
          {verdictReasoning && (
            <p style={{ color: C.text2, fontSize: 14, lineHeight: 1.6, maxWidth: 480, margin: '0 auto' }}>
              {verdictReasoning}
            </p>
          )}
          {!verdictReasoning && validationScore !== null && (
            <p style={{ color: C.text2, fontSize: 14 }}>
              Validation Score: {effectiveScore}/100
            </p>
          )}
        </div>

        {/* Score contradiction explainer */}
        {verdict === 'BUILD' && validationScore !== null && validationScore < 50 && (
          <div style={{
            background: 'rgba(255,184,0,0.08)', border: '1px solid rgba(255,184,0,0.25)',
            borderRadius: 14, padding: '16px 20px', marginBottom: 20, textAlign: 'left',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 16 }}>ℹ️</span>
              <span style={{ color: C.amber, fontSize: 13, fontWeight: 700 }}>Why BUILD with a low score?</span>
            </div>
            <p style={{ color: C.text2, fontSize: 13, lineHeight: 1.6 }}>
              Your reality check score ({validationScore}/100) measures market readiness from public data, but your validation results
              {paidConversions > 0 ? ` (${paidConversions} conversions)` : ''}{preOrders > 0 ? ` and ${preOrders} pre-orders` : ''} show
              real demand from actual users — which is a stronger signal.
            </p>
          </div>
        )}

        {/* Score Summary Bars */}
        {scoreEntries.length > 0 && (
          <div style={{
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16,
            padding: 24, marginBottom: 20, textAlign: 'left',
          }}>
            <h3 style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Score Breakdown</h3>
            {scoreEntries.map(([key, score]) => (
              <ScoreBar
                key={key}
                label={key.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()).trim()}
                score={typeof score === 'number' ? score : 0}
                color={(typeof score === 'number' ? score : 0) >= 70 ? C.green : (typeof score === 'number' ? score : 0) >= 50 ? C.amber : C.danger}
              />
            ))}
          </div>
        )}

        {/* What The Data Says */}
        {(dataInsights.length > 0 || whatWorks.length > 0) && (
          <div style={{
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16,
            padding: 24, marginBottom: 20, textAlign: 'left',
          }}>
            <h3 style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 14 }}>What The Data Says</h3>
            {dataInsights.slice(0, 5).map((insight, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                <span style={{ color: C.green, flexShrink: 0 }}>+</span>
                <span style={{ color: C.text2, fontSize: 13, lineHeight: 1.5 }}>{insight}</span>
              </div>
            ))}
            {warningSignals.length > 0 && warningSignals.map((w, i) => (
              <div key={`w-${i}`} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                <span style={{ color: C.amber, flexShrink: 0 }}>!</span>
                <span style={{ color: C.text2, fontSize: 13, lineHeight: 1.5 }}>{w}</span>
              </div>
            ))}
          </div>
        )}

        {/* Next Steps based on verdict */}
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16,
          padding: 24, marginBottom: 20, textAlign: 'left',
        }}>
          <h3 style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 14 }}>
            {verdict === 'BUILD' ? 'Next Steps: Build Your MVP' : verdict === 'PIVOT' ? 'Next Steps: Explore Pivots' : 'Next Steps: What You Learned'}
          </h3>
          {verdict === 'BUILD' && (
            <>
              <p style={{ color: C.text2, fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>
                The data supports moving forward. Generate your personalized MVP Blueprint from the dashboard to get a step-by-step build plan tailored to your skills, budget, and validated idea.
              </p>
              {whatWorks.length > 0 && whatWorks.map((w, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                  <span style={{ color: C.green, flexShrink: 0 }}>✓</span>
                  <span style={{ color: C.text2, fontSize: 13 }}>{w}</span>
                </div>
              ))}
            </>
          )}
          {verdict === 'PIVOT' && (
            <>
              <p style={{ color: C.text2, fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>
                {fullAnalysis || 'The signals are mixed. Consider adjusting your target audience, pricing, or value proposition before investing in building.'}
              </p>
              {whatToAdjust.length > 0 && whatToAdjust.map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                  <span style={{ color: C.amber, flexShrink: 0 }}>→</span>
                  <span style={{ color: C.text2, fontSize: 13 }}>{a}</span>
                </div>
              ))}
              {nextExperiment && (
                <div style={{ background: `${C.primary}0d`, border: `1px solid ${C.primary}30`, borderRadius: 10, padding: 14, marginTop: 12 }}>
                  <p style={{ color: C.primary, fontSize: 11, fontWeight: 700, marginBottom: 4 }}>Suggested Next Experiment</p>
                  <p style={{ color: C.text2, fontSize: 13 }}>{nextExperiment}</p>
                </div>
              )}
            </>
          )}
          {verdict === 'KILL' && (
            <>
              <p style={{ color: C.text2, fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>
                {fullAnalysis || "The data doesn't support this idea in its current form. That's valuable information — here's what you learned that you can apply to your next idea."}
              </p>
              {encouragingInsights.length > 0 && encouragingInsights.map((e, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                  <span style={{ color: C.text3, flexShrink: 0 }}>•</span>
                  <span style={{ color: C.text2, fontSize: 13 }}>{e}</span>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Validation Results Summary (if submitted) */}
        {hasValidationResults && (
          <div className="r-grid-4" style={{
            gap: 10, marginBottom: 24,
          }}>
            {[
              { label: 'Visits', value: userResults!.visits ?? 0, color: C.primary },
              { label: 'Conversions', value: paidConversions, color: C.green },
              { label: 'Emails', value: userResults!.emailsCollected ?? 0, color: C.accent },
              { label: 'Pre-orders', value: preOrders, color: C.amber },
            ].map(({ label, value, color }) => (
              <div key={label} style={{
                background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 8px',
                textAlign: 'center',
              }}>
                <div style={{ color, fontSize: 24, fontWeight: 800 }}>{value}</div>
                <div style={{ color: C.text3, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Share Your Score */}
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16,
          padding: 24, marginBottom: 20, textAlign: 'center',
        }}>
          <h3 style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Share Your Score</h3>
          <p style={{ color: C.text2, fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
            {`Just validated my startup idea "${productName}" — verdict: ${verdict}${paidConversions > 0 ? `, ${paidConversions} conversions` : ''}${preOrders > 0 ? `, ${preOrders} pre-orders` : ''}. Built with @StartupScoreLive`}
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button
              onClick={() => {
                const text = `Just validated my startup idea "${productName}" — verdict: ${verdict}${paidConversions > 0 ? `, ${paidConversions} conversions` : ''}${preOrders > 0 ? `, ${preOrders} pre-orders` : ''}. Built with @StartupScoreLive #buildinpublic`;
                window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
              }}
              style={{
                background: 'rgba(29,161,242,0.12)', border: '1px solid rgba(29,161,242,0.3)',
                borderRadius: 10, color: '#1DA1F2', fontSize: 13, fontWeight: 600,
                padding: '10px 18px', cursor: 'pointer',
              }}
            >
              Share on X →
            </button>
            <button
              onClick={() => {
                window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://startupscore.live')}`, '_blank');
              }}
              style={{
                background: 'rgba(10,102,194,0.12)', border: '1px solid rgba(10,102,194,0.3)',
                borderRadius: 10, color: '#0A66C2', fontSize: 13, fontWeight: 600,
                padding: '10px 18px', cursor: 'pointer',
              }}
            >
              Share on LinkedIn →
            </button>
          </div>
        </div>

        {/* What's Next CTAs */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
          <button
            onClick={() => {
              if (pipeline.linkedStartupId) {
                navigate(`/startups/${pipeline.linkedStartupId}/dashboard`);
              } else {
                navigate('/pipeline');
              }
            }}
            style={{
              width: '100%', maxWidth: 400,
              background: C.orange,
              border: 'none', borderRadius: 14, color: '#fff',
              fontSize: 17, fontWeight: 700, padding: '18px 28px', cursor: 'pointer',
              boxShadow: `0 4px 24px ${C.orangeGlow}`,
              animation: 'pulseGlow 3s ease-in-out infinite',
            }}
          >
            → View Full Dashboard
          </button>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
            {pipeline.linkedStartupId && (
              <button
                onClick={() => navigate(`/startups/${pipeline.linkedStartupId}/dashboard`)}
                style={{
                  background: 'transparent', border: `1px solid ${C.primary}`, borderRadius: 12,
                  color: C.primary, fontSize: 13, fontWeight: 600, padding: '12px 18px', cursor: 'pointer',
                }}
              >
                Run Intelligence Analysis
              </button>
            )}
            <button
              onClick={() => window.print()}
              style={{
                background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 12,
                color: C.text2, fontSize: 13, padding: '12px 18px', cursor: 'pointer',
              }}
            >
              Download PDF Report
            </button>
            <button
              onClick={onDismiss}
              style={{
                background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 12,
                color: C.text2, fontSize: 13, padding: '12px 18px', cursor: 'pointer',
              }}
            >
              ← Back to Pipeline
            </button>
          </div>
          {/* Auto-redirect countdown */}
          {pipeline.linkedStartupId && redirectIn > 0 && !redirectPaused && (
            <button
              onClick={() => setRedirectPaused(true)}
              style={{
                background: 'none', border: 'none', color: C.text3, fontSize: 12,
                cursor: 'pointer', marginTop: 4,
              }}
            >
              Redirecting to dashboard in {redirectIn}s · Click to stay
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Step indicator (top bar) ───────────────────────────────────

const STEP_LABELS = ['Profile', 'Idea', 'Reality', 'Position', 'Validate'];

function StepIndicator({ current, max, onStepClick }: { current: number; max: number; onStepClick: (s: number) => void }) {
  const prevCurrentRef = useRef(current);
  const [justCompleted, setJustCompleted] = useState<number | null>(null);

  useEffect(() => {
    if (current > prevCurrentRef.current) {
      const completedStep = prevCurrentRef.current;
      setJustCompleted(completedStep);
      const timer = setTimeout(() => setJustCompleted(null), 600);
      prevCurrentRef.current = current;
      return () => clearTimeout(timer);
    }
    prevCurrentRef.current = current;
  }, [current]);

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
      {STEP_LABELS.map((label, i) => {
        const done = i < current;
        const active = i === current;
        const locked = i > max;
        const isJustCompleted = justCompleted === i;
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, position: 'relative' }}>
              {/* Pulse ring on completion */}
              {isJustCompleted && (
                <div style={{
                  position: 'absolute', top: 0, left: 0, width: 28, height: 28,
                  borderRadius: '50%', border: `2px solid ${C.primary}`,
                  animation: 'step-complete-ring 0.6s ease forwards',
                  pointerEvents: 'none',
                }} />
              )}
              <button
                onClick={() => !locked && onStepClick(i)}
                title={done ? `View ${label} results` : label}
                style={{
                  width: 28, height: 28, borderRadius: '50%',
                  border: `2px solid ${done || active ? C.primary : C.border}`,
                  background: done ? C.primary : 'transparent',
                  cursor: locked ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.25s',
                  flexShrink: 0,
                  transform: isJustCompleted ? undefined : 'scale(1)',
                  animation: isJustCompleted ? 'step-complete-pop 0.3s ease' : undefined,
                }}
              >
                {done ? (
                  <span style={{
                    color: '#fff', fontSize: 11, fontWeight: 700,
                    opacity: isJustCompleted ? undefined : 1,
                    animation: isJustCompleted ? 'fadeIn 0.3s ease' : undefined,
                  }}>✓</span>
                ) : active ? (
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%', background: C.primary, display: 'block',
                    animation: 'glow-pulse 1.5s ease infinite',
                  }} />
                ) : (
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: locked ? C.text3 : C.text2, display: 'block' }} />
                )}
              </button>
              <span style={{
                fontSize: 8, fontWeight: 600, letterSpacing: 0.3,
                color: done ? C.primary : active ? C.text : C.text3,
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                display: window.innerWidth < 480 ? 'none' : 'block',
              }}>
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div style={{
                width: 22, height: 2, marginTop: 13, flexShrink: 0,
                background: i < current ? C.primary : C.border,
                transition: 'background 0.4s',
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── AI Thinking Screen ─────────────────────────────────────────

const AI_THINKING_MESSAGES: Record<number, string[]> = {
  0: ['Analyzing your experience…', 'Building your founder profile…', 'Personalizing recommendations…'],
  1: ['Reading your idea description…', 'Identifying market gaps…', 'Spotting the opportunity…'],
  2: ['Searching the web for competitors…', 'Checking market saturation…', 'Estimating demand signals…', 'Cross-referencing similar products…'],
  3: ['Mapping your competitive landscape…', 'Finding your positioning angle…', 'Crafting differentiation strategies…'],
  4: ['Designing your validation plan…', 'Building your landing page guide…', 'Crafting interview questions…'],
  5: ['Reviewing your validation data…', 'Scoring market signals…', 'Analyzing conversion metrics…'],
  6: ['Generating your MVP blueprint…', 'Selecting your tech stack…', 'Scoping your build checklist…', 'Calculating your budget…'],
};

function AIThinkingScreen({ step, stage }: { step: number; stage: string }) {
  const messages = AI_THINKING_MESSAGES[step] ?? ['Processing…'];
  const [msgIdx, setMsgIdx] = useState(0);
  const [barWidth, setBarWidth] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMsgIdx(prev => (prev + 1) % messages.length);
    }, 2200);
    return () => clearInterval(interval);
  }, [messages.length]);

  // Animated progress: start fast then slow down, never exceeding 90%
  useEffect(() => {
    setBarWidth(0);
    const steps = [
      { pct: 15, delay: 500 },
      { pct: 35, delay: 2000 },
      { pct: 55, delay: 4000 },
      { pct: 75, delay: 7000 },
      { pct: 88, delay: 12000 },
    ];
    const timers = steps.map(({ pct, delay }) =>
      setTimeout(() => setBarWidth(pct), delay)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '80px 24px', textAlign: 'center', animation: 'fadeUp 0.3s ease',
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: '50%', marginBottom: 32,
        background: `conic-gradient(from 0deg, ${C.primary}, ${C.accent}, rgba(124,111,247,0.1), ${C.primary})`,
        animation: 'spin 1.2s linear infinite',
        position: 'relative',
      }}>
        <div style={{ width: 48, height: 48, background: C.bg, borderRadius: '50%', position: 'absolute', top: 8, left: 8 }} />
      </div>
      <p style={{ color: C.text3, fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
        {stage}
      </p>
      <p key={msgIdx} style={{ color: C.text2, fontSize: 16, marginBottom: 32, animation: 'fadeUp 0.3s ease', minHeight: 24 }}>
        {messages[msgIdx]}
      </p>
      <div style={{ width: '100%', maxWidth: 320, height: 3, background: C.surface2, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 3,
          background: `linear-gradient(90deg, ${C.primary}, ${C.accent})`,
          width: `${barWidth}%`,
          transition: 'width 1.2s ease',
        }} />
      </div>
    </div>
  );
}

// ── Confetti burst ─────────────────────────────────────────────

function Confetti({ active }: { active: boolean }) {
  if (!active) return null;
  const pieces = Array.from({ length: 22 }, (_, i) => ({
    color: [C.primary, C.accent, C.green, C.amber, '#ff6b6b', '#c084fc'][i % 6],
    left: `${(i / 22) * 100}%`,
    delay: `${(i * 0.07).toFixed(2)}s`,
    size: 6 + (i % 4) * 2,
  }));
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 999, overflow: 'hidden' }}>
      {pieces.map((p, i) => (
        <div key={i} style={{
          position: 'absolute', top: '-20px', left: p.left,
          width: p.size, height: p.size, borderRadius: 2,
          background: p.color,
          animation: `confetti-fall 3s ${p.delay} ease forwards`,
        }} />
      ))}
    </div>
  );
}

// ── STEP 0: User Profiling ────────────────────────────────────

const PROFILE_Q1 = [
  { value: 'none', icon: '🔰', label: 'Complete beginner', sub: "I can use tools, not build them" },
  { value: 'low', icon: '🎨', label: 'Low', sub: 'HTML/CSS basics, no-code tools' },
  { value: 'medium', icon: '⚡', label: 'Medium', sub: 'I can build simple apps' },
  { value: 'high', icon: '💻', label: 'High', sub: "I'm a developer" },
  { value: 'expert', icon: '🚀', label: 'Expert', sub: "I've shipped multiple products" },
];
const PROFILE_Q2 = [
  { value: '<500', icon: '💸', label: 'Under $500', sub: 'Bootstrap mode' },
  { value: '500-2k', icon: '💰', label: '$500 – $2,000', sub: 'Lean startup' },
  { value: '2k-10k', icon: '🏦', label: '$2,000 – $10,000', sub: 'Funded start' },
  { value: '10k-50k', icon: '💎', label: '$10k – $50k', sub: 'Well resourced' },
  { value: '50k+', icon: '🏢', label: '$50,000+', sub: 'Serious investment' },
];
const PROFILE_Q3 = [
  { value: '<5h/week', icon: '⏰', label: 'Less than 5h/week', sub: 'Side project' },
  { value: '5-20h/week', icon: '📅', label: '5–20h/week', sub: 'Part-time' },
  { value: '20-40h/week', icon: '🗓', label: '20–40h/week', sub: 'Near full-time' },
  { value: 'fulltime', icon: '🔥', label: 'Full-time', sub: 'All in' },
];
const PROFILE_Q4 = [
  { value: 'none', icon: '🌱', label: 'First time', sub: 'Complete beginner' },
  { value: 'side_project', icon: '💡', label: "Side projects", sub: "I've built things before" },
  { value: 'failed_startup', icon: '😤', label: 'Failed startup', sub: 'I learned the hard way' },
  { value: 'successful_startup', icon: '🏆', label: 'Successful product', sub: "I've shipped and won" },
];
const PROFILE_Q5_CREATOR = [
  { value: 'designer', icon: '🎨', label: 'Designer' },
  { value: 'developer', icon: '💻', label: 'Developer' },
  { value: 'marketer', icon: '📣', label: 'Marketer' },
  { value: 'domain_expert', icon: '🎓', label: 'Domain expert' },
  { value: 'generalist', icon: '🌍', label: 'Generalist' },
];

function OptionCard({ option, selected, onSelect }: {
  option: { value: string; icon: string; label: string; sub?: string };
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      style={{
        background: selected ? `${C.primary}18` : C.surface,
        border: `2px solid ${selected ? C.primary : C.border}`,
        borderRadius: 14,
        padding: '14px 18px',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 0.15s',
        boxShadow: selected ? `0 0 0 3px ${C.primaryGlow}` : 'none',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
      }}
    >
      <span style={{ fontSize: 22 }}>{option.icon}</span>
      <div>
        <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>{option.label}</div>
        {option.sub && <div style={{ color: C.text2, fontSize: 12, marginTop: 2 }}>{option.sub}</div>}
      </div>
    </button>
  );
}

function Step0Profile({ pipeline, onDone }: { pipeline: Pipeline; onDone: (updated: Pipeline) => void }) {
  const [q, setQ] = useState(1);
  const [answers, setAnswers] = useState({
    technicalSkill: pipeline.userProfile?.technicalSkill ?? '',
    budget: pipeline.userProfile?.budget ?? '',
    timeAvailable: pipeline.userProfile?.timeAvailable ?? '',
    previousExperience: pipeline.userProfile?.previousExperience ?? '',
    creatorType: pipeline.userProfile?.creatorType ?? '',
    ideasInDrawer: pipeline.userProfile?.ideasInDrawer ?? 5,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(pipeline.userProfile?.aiOutput ?? null);
  const [error, setError] = useState('');
  const [creditModal, setCreditModal] = useState<{ required: number; available: number } | null>(null);
  const { optimisticDeduct, optimisticRestore, fetchBalance } = useCreditsStore();
  const { advancing, advanceError, clearAdvanceError, advance } = useContinue(pipeline.id, onDone);
  const { steps: profileSteps, progress: profileProgress, finish } = useLoadingSteps([
    'Reading your answers…',
    'Building founder archetype…',
    'Identifying strengths…',
    'Mapping risk patterns…',
    'Personalizing recommendations…',
  ], loading);

  const select = (field: string, value: string) => {
    const updated = { ...answers, [field]: value };
    setAnswers(updated);
    if (q < 5) setTimeout(() => setQ(q + 1), 300);
  };

  const submit = async () => {
    setLoading(true);
    setError('');
    // FIX: #1 — optimistic credit deduct before AI call, restore on failure
    const COST = CREDIT_COSTS.PIPELINE_PROFILE;
    let restored = false;
    optimisticDeduct(COST);
    const MAX_RETRIES = 1;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) await new Promise(r => setTimeout(r, 1500 * attempt));
        console.log('[Step0] Submitting profile:', answers);
        const { data } = await aiApi.post(`/pipeline/${pipeline.id}/profile`, { ...answers });
        console.log('[Step0] Profile response:', data);
        const aiOutput = data?.data?.pipeline?.userProfile?.aiOutput;
        if (!aiOutput?.founderType) throw new Error('Profile analysis incomplete — founderType missing. Try again.');
        finish();
        setTimeout(() => {
          setResult(aiOutput);
          setLoading(false);
        }, 400);
        return;
      } catch (err: any) {
        console.error('[Step0] Submit error:', err);
        const is5xx = err?.response?.status >= 500;
        const is429 = err?.response?.status === 429;
        const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout');
        if ((is5xx || is429 || isTimeout) && attempt < MAX_RETRIES) { await new Promise(r => setTimeout(r, 2000 * (attempt + 1))); continue; }
        // FIX: #1 — restore credits on failure
        if (!restored) { optimisticRestore(COST); restored = true; }
        if (err?.response?.status === 402) {
          setCreditModal({ required: err.response.data.required, available: err.response.data.available });
        } else {
          setError(is5xx || is429 ? 'High demand right now — please wait 30 seconds and try again. Your credits were not deducted.' : classifyApiError(err));
        }
        fetchBalance();
        setLoading(false);
        return;
      }
    }
  };

  const allAnswered = answers.technicalSkill && answers.budget && answers.timeAvailable && answers.previousExperience && answers.creatorType;

  if (loading) {
    return <LoadingCinematic stage="Reading your founder DNA..." steps={profileSteps} progress={profileProgress} subtitle="Building your personalized profile" creditReassurance />;
  }

  if (result) {
    const r = result as Record<string, unknown>;
    return (
      <div style={{ maxWidth: 640, margin: '0 auto', animation: 'fadeUp 0.5s ease' }}>
        {/* Hero */}
        <div style={{
          background: `linear-gradient(135deg, ${C.primary}18, ${C.accent}10)`,
          border: `1px solid ${C.primary}40`,
          borderRadius: 20,
          padding: '40px 36px',
          textAlign: 'center',
          marginBottom: 20,
          animation: 'float 4s ease-in-out infinite',
        }}>
          <div style={{ fontSize: 12, color: C.primary, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>
            You are a
          </div>
          <h2 style={{
            fontSize: 44, fontWeight: 900,
            background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            marginBottom: 16, letterSpacing: -1,
          }}>
            THE {String(r.founderType ?? '').toUpperCase()}
          </h2>
          <p style={{ color: C.text, fontSize: 15, lineHeight: 1.7, marginBottom: 10 }}>{S(r.executionStyle)}</p>
          <p style={{ color: C.text2, fontSize: 14, lineHeight: 1.5 }}>{S(r.riskPattern)}</p>
          <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
            {answers.technicalSkill && <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, background: `${C.primary}15`, color: C.primary }}>{answers.technicalSkill}</span>}
            {answers.budget && <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, background: `${C.accent}15`, color: C.accent }}>{answers.budget}</span>}
            {answers.timeAvailable && <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, background: `${C.green}15`, color: C.green }}>{answers.timeAvailable}</span>}
            {answers.creatorType && <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, background: `${C.amber}15`, color: C.amber }}>{answers.creatorType}</span>}
          </div>
        </div>

        {/* Strengths / Challenges */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
          {Array.isArray(r.founderStrengths) && (
            <Card style={{ background: `${C.green}0d`, border: `1px solid ${C.green}30`, padding: 20 }}>
              <h4 style={{ color: C.green, fontSize: 12, fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Strengths</h4>
              {SA(r.founderStrengths).map((s, i) => (
                <div key={i} style={{ color: C.text2, fontSize: 13, marginBottom: 6 }}>✓ {s}</div>
              ))}
            </Card>
          )}
          {Array.isArray(r.founderChallenges) && (
            <Card style={{ background: `${C.amber}0d`, border: `1px solid ${C.amber}30`, padding: 20 }}>
              <h4 style={{ color: C.amber, fontSize: 12, fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Watch out for</h4>
              {SA(r.founderChallenges).map((s, i) => (
                <div key={i} style={{ color: C.text2, fontSize: 13, marginBottom: 6 }}>⚠ {s}</div>
              ))}
            </Card>
          )}
        </div>

        {/* Suggestions */}
        {Array.isArray(r.personalizedSuggestions) && (
          <Card style={{ marginBottom: 16 }}>
            <h3 style={{ color: C.text, fontSize: 15, fontWeight: 700, marginBottom: 16 }}>💡 Personalized for you</h3>
            {SA(r.personalizedSuggestions).map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                <span style={{ color: C.primary, fontWeight: 700, flexShrink: 0 }}>→</span>
                <span style={{ color: C.text2, fontSize: 14, lineHeight: 1.5 }}>{s}</span>
              </div>
            ))}
          </Card>
        )}

        {!!r.encouragement && (
          <div style={{
            background: `${C.primary}0d`, border: `1px solid ${C.primary}30`,
            borderRadius: 14, padding: 20, marginBottom: 24,
            color: '#c4b5fd', fontSize: 14, fontStyle: 'italic', lineHeight: 1.7,
          }}>
            "{S(r.encouragement)}"
          </div>
        )}

        <ErrMsg msg={advanceError} onDismiss={clearAdvanceError} />
        <div style={{ display: 'flex', gap: 12 }}>
          <GhostBtn onClick={() => { setResult(null); setQ(1); }}>← That's not quite me</GhostBtn>
          <button
            onClick={() => advance((p) => !p.userProfile?.aiOutput?.founderType ? 'Profile analysis incomplete. Try again.' : null)}
            disabled={advancing}
            style={{
              flex: 2, background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`,
              border: 'none', borderRadius: 12, color: '#fff', fontSize: 16, fontWeight: 700,
              padding: '14px 0', cursor: advancing ? 'not-allowed' : 'pointer',
              opacity: advancing ? 0.7 : 1,
            }}
          >
            {advancing ? 'Saving…' : 'This is me → Continue'}
          </button>
        </div>
      </div>
    );
  }

  const questions = [
    { num: 1, title: "What's your technical background?", options: PROFILE_Q1, field: 'technicalSkill' },
    { num: 2, title: "What's your budget for this project?", options: PROFILE_Q2, field: 'budget' },
    { num: 3, title: 'How much time can you dedicate?', options: PROFILE_Q3, field: 'timeAvailable' },
    { num: 4, title: 'Have you built startups before?', options: PROFILE_Q4, field: 'previousExperience' },
  ];

  if (q === 5) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', animation: 'fadeUp 0.35s ease' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <p style={{ color: C.text3, fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>Question 5 of 5</p>
          <h2 style={{ fontSize: 26, fontWeight: 800, color: C.text }}>What best describes you?</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 32 }}>
          {PROFILE_Q5_CREATOR.map((opt) => (
            <OptionCard key={opt.value} option={{ ...opt, sub: undefined }} selected={answers.creatorType === opt.value} onSelect={() => setAnswers({ ...answers, creatorType: opt.value })} />
          ))}
        </div>

        <div style={{ borderTop: `1px solid ${C.border}`, margin: '0 0 24px', paddingTop: 20 }}>
          <p style={{ color: C.text3, fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>Bonus</p>
        </div>

        <Card style={{ marginBottom: 28 }}>
          <h3 style={{ color: C.text, fontSize: 15, fontWeight: 700, marginBottom: 20 }}>How many ideas do you have right now?</h3>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <span style={{
              fontSize: 56, fontWeight: 900,
              background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              {answers.ideasInDrawer}
            </span>
            <div style={{ color: C.text2, fontSize: 13, marginTop: 4 }}>
              {answers.ideasInDrawer <= 1 ? 'Laser focused' : answers.ideasInDrawer <= 5 ? 'Good selection' : answers.ideasInDrawer <= 10 ? 'Idea machine' : 'Serial ideator 🤯'}
            </div>
          </div>
          <input type="range" min={1} max={20} value={answers.ideasInDrawer}
            onChange={(e) => setAnswers({ ...answers, ideasInDrawer: parseInt(e.target.value) })}
            style={{ width: '100%', accentColor: C.primary }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', color: C.text3, fontSize: 12, marginTop: 4 }}>
            <span>1</span><span>20+</span>
          </div>
        </Card>

        {/* FIX: #7 — retry button on step error */}
        <ErrMsg msg={error} onRetry={submit} />
        <GradientBtn onClick={submit} disabled={!allAnswered || loading}>
          Analyze My Profile →<CreditCostLabel step={0} isFirst={pipeline.isFirstPipeline} />
        </GradientBtn>
        {creditModal && <InsufficientCreditsModal action="PIPELINE_PROFILE" required={creditModal.required} available={creditModal.available} onClose={() => { setCreditModal(null); fetchBalance(); }} />}
      </div>
    );
  }

  const currentQ = questions[q - 1];
  return (
    <div style={{ maxWidth: 600, margin: '0 auto', animation: 'fadeUp 0.35s ease' }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <p style={{ color: C.text3, fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>Question {q} of 5</p>
        <h2 style={{ fontSize: 26, fontWeight: 800, color: C.text }}>{currentQ.title}</h2>
      </div>
      <div style={{ display: 'grid', gap: 10 }}>
        {currentQ.options.map((opt) => (
          <OptionCard key={opt.value} option={opt}
            selected={(answers as Record<string, unknown>)[currentQ.field] === opt.value}
            onSelect={() => select(currentQ.field, opt.value)} />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 28, justifyContent: 'center' }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <div key={n} style={{
            width: n <= q ? 20 : 8, height: 8, borderRadius: 4,
            background: n <= q ? C.primary : C.surface2,
            transition: 'all 0.3s',
          }} />
        ))}
      </div>
    </div>
  );
}

// ── STEP 1: Idea Capture ──────────────────────────────────────

function Step1Idea({ pipeline, onDone }: { pipeline: Pipeline; onDone: (updated: Pipeline) => void }) {
  const { updatePipeline } = usePipelineStore();
  const [rawIdea, setRawIdea] = useState(pipeline.idea?.rawIdea ?? '');
  const [answers, setAnswers] = useState({
    problemSolved: pipeline.idea?.guidedAnswers?.problemSolved ?? '',
    whoSuffers: pipeline.idea?.guidedAnswers?.whoSuffers ?? '',
    currentSolution: pipeline.idea?.guidedAnswers?.currentSolution ?? '',
    promisedResult: pipeline.idea?.guidedAnswers?.promisedResult ?? '',
    budgetAndTime: pipeline.idea?.guidedAnswers?.budgetAndTime ?? '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Record<string, unknown> | null>(
    pipeline.idea?.aiOutput ? pipeline.idea.aiOutput as Record<string, unknown> : null
  );
  const [error, setError] = useState('');
  // Sub-screen state for split Step 2
  const hasExistingAnswers = !!(pipeline.idea?.guidedAnswers?.problemSolved);
  const [subScreen, setSubScreen] = useState<'a' | 'b'>(hasExistingAnswers ? 'b' : 'a');
  const [readingIdea, setReadingIdea] = useState(false);
  const [showGuided, setShowGuided] = useState(!!pipeline.idea?.rawIdea);
  const [autoFilling, setAutoFilling] = useState(false);
  const [toast, setToast] = useState('');
  const [flashFields, setFlashFields] = useState<Set<string>>(new Set());
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [creditModal, setCreditModal] = useState<{ required: number; available: number } | null>(null);
  const { optimisticDeduct, optimisticRestore, fetchBalance } = useCreditsStore();
  const { advancing, advanceError, clearAdvanceError, advance } = useContinue(pipeline.id, onDone);
  const { steps: ideaSteps, progress: ideaProgress, finish } = useLoadingSteps([
    'Parsing idea description…',
    'Extracting problem signal…',
    'Identifying target audience…',
    'Scoring clarity…',
    'Generating structured summary…',
  ], loading);

  // Animated clarity score — must be called unconditionally (Rules of Hooks)
  const clarityScore = (result?.clarityScore as number) ?? 0;
  const clarityAnim = useAnimatedScore(clarityScore);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3200);
  };

  const autoFillFromIdea = async () => {
    if (rawIdea.length < 30) return;
    setAutoFilling(true);
    try {
      const { data } = await api.post('/pipeline/extract-idea', { rawIdea });
      const extracted = data?.data ?? {};
      const newAnswers = {
        problemSolved: extracted.problemSolved || '',
        whoSuffers: extracted.whoSuffers || '',
        currentSolution: extracted.currentSolution || '',
        promisedResult: extracted.promisedResult || '',
        budgetAndTime: extracted.budgetAndTime || answers.budgetAndTime,
      };
      setAnswers(newAnswers);
      setFieldErrors({});
      // Flash all filled fields
      const filled = new Set(
        Object.entries(newAnswers)
          .filter(([, v]) => v)
          .map(([k]) => k)
      );
      setFlashFields(filled);
      setTimeout(() => setFlashFields(new Set()), 700);
      showToast('✨ Fields populated! Review and adjust as needed.');
    } catch {
      showToast('Could not auto-fill. Please fill manually.');
    } finally {
      setAutoFilling(false);
    }
  };

  const analyseAndTransition = async () => {
    if (rawIdea.trim().length < 10) return;
    setReadingIdea(true);
    try {
      // Auto-fill from idea if enough content
      if (rawIdea.length >= 30) {
        const { data } = await api.post('/pipeline/extract-idea', { rawIdea });
        const extracted = data?.data ?? {};
        setAnswers({
          problemSolved: extracted.problemSolved || '',
          whoSuffers: extracted.whoSuffers || '',
          currentSolution: extracted.currentSolution || '',
          promisedResult: extracted.promisedResult || '',
          budgetAndTime: extracted.budgetAndTime || answers.budgetAndTime,
        });
      }
    } catch {
      // Pre-fill failed — user will fill manually
    }
    // Ensure minimum 1.5s animation
    await new Promise(r => setTimeout(r, 1500));
    setReadingIdea(false);
    setSubScreen('b');
  };

  const allAnswered = Object.values(answers).every((v) => v.trim().length > 0) && rawIdea.trim().length > 10;

  useEffect(() => { if (rawIdea.trim().length > 20) setShowGuided(true); }, [rawIdea]);

  const submit = async () => {
    setLoading(true);
    setError('');
    // FIX: #1 — optimistic credit deduct before AI call, restore on failure
    const COST = CREDIT_COSTS.PIPELINE_IDEA;
    let restored = false;
    optimisticDeduct(COST);

    const MAX_RETRIES = 1;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) {
          console.log(`[Step1] Retry ${attempt}/${MAX_RETRIES}`);
          await new Promise(r => setTimeout(r, 1500 * attempt));
        }
        console.log('[Step1] Submitting idea:', { rawIdea: rawIdea.slice(0, 60) });
        const { data } = await aiApi.post(`/pipeline/${pipeline.id}/idea`, { rawIdea, guidedAnswers: answers });
        console.log('[Step1] Idea response:', data);
        const aiOutput = data?.data?.pipeline?.idea?.aiOutput;
        if (!aiOutput?.clarityScore && aiOutput?.clarityScore !== 0) throw new Error('Idea structuring incomplete — clarity score missing. Try again.');
        finish();
        // B1: Auto-create startup in background if none linked yet
        if (!pipeline.linkedStartupId) {
          const placeholderName = `Idea from ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
          const segment = deriveTargetSegment([rawIdea, answers.whoSuffers, answers.problemSolved].join(' '));
          api.post('/startups', {
            name: placeholderName,
            description: rawIdea.trim().slice(0, 500),
            targetSegment: segment,
            problemStatement: answers.problemSolved,
            fromPipelineId: pipeline.id,
          }).then(({ data: sRes }) => {
            const newId: string = sRes.data.startup.id;
            api.patch(`/pipeline/${pipeline.id}`, { linkedStartupId: newId }).catch(() => {});
            updatePipeline({ ...pipeline, linkedStartupId: newId });
          }).catch(() => {});
        }
        setTimeout(() => { setResult(aiOutput); setLoading(false); }, 400);
        return; // success — exit retry loop
      } catch (err: any) {
        console.error(`[Step1] Submit error (attempt ${attempt + 1}):`, err);
        const is5xx = err?.response?.status >= 500;
        const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout');
        // Only retry on server errors or timeouts
        if ((is5xx || isTimeout) && attempt < MAX_RETRIES) continue;

        // FIX: #1 — restore credits on failure
        if (!restored) { optimisticRestore(COST); restored = true; }
        if (err?.response?.status === 402) {
          const d = err.response.data;
          setCreditModal({ required: d.required, available: d.available });
        } else {
          const msg = classifyApiError(err);
          setError(is5xx || isTimeout
            ? 'High demand right now — please wait 30 seconds and try again. Your credits were not deducted.'
            : msg);
        }
        fetchBalance();
        setLoading(false);
        return;
      }
    }
  };

  if (loading) {
    return <LoadingCinematic stage="Structuring your idea..." steps={ideaSteps} progress={ideaProgress} subtitle="AI is analyzing your concept" creditReassurance />;
  }

  if (result) {
    const r = result;
    const score = (r.clarityScore as number) ?? 0;
    const scoreColor = score >= 91 ? '#a855f7' : score >= 76 ? C.green : score >= 50 ? C.amber : C.danger;
    const scoreLabel = score >= 91 ? 'Crystal clear' : score >= 76 ? 'Clear idea' : score >= 50 ? 'Good start' : 'Needs work';
    const circumference = 2 * Math.PI * 44;
    const animDash = circumference - (clarityAnim.value / 100) * circumference;

    return (
      <div style={{ maxWidth: 800, margin: '0 auto', animation: 'fadeUp 0.4s ease' }}>
        {/* Raw vs structured */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16, marginBottom: 16 }}>
          <Card style={{ background: C.surface2, padding: 20 }}>
            <p style={{ color: C.text3, fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>Raw idea</p>
            <p style={{ color: C.text2, fontSize: 13, lineHeight: 1.6 }}>{rawIdea}</p>
          </Card>
          <Card style={{ border: `1px solid ${C.primary}40`, padding: 20 }}>
            <p style={{ color: C.primary, fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>Structured version</p>
            <p style={{ color: C.text, fontSize: 15, lineHeight: 1.7, fontWeight: 500 }}>{S(r.ideaSnapshot)}</p>
          </Card>
        </div>

        {/* Score + fields */}
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 16, marginBottom: 16 }}>
          <Card style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 20 }}><div ref={clarityAnim.ref}></div>
            <svg width={100} height={100} viewBox="0 0 100 100">
              <circle cx={50} cy={50} r={44} fill="none" stroke={C.surface2} strokeWidth={10} />
              <circle cx={50} cy={50} r={44} fill="none" stroke={scoreColor} strokeWidth={10}
                strokeDasharray={circumference} strokeDashoffset={animDash}
                strokeLinecap="round" transform="rotate(-90 50 50)" />
              <text x={50} y={46} textAnchor="middle" fill={scoreColor} fontSize={22} fontWeight={700}>{clarityAnim.value}</text>
              {clarityAnim.labelVisible && (
                <text x={50} y={62} textAnchor="middle" fill={C.text2} fontSize={9} style={{ animation: 'fadeIn 0.3s ease' }}>{scoreLabel}</text>
              )}
            </svg>
            <p style={{ color: C.text2, fontSize: 11, textAlign: 'center', marginTop: 6 }}>Clarity Score</p>
          </Card>
          <div style={{ display: 'grid', gap: 10 }}>
            <Card style={{ padding: 16 }}>
              <p style={{ color: C.text2, fontSize: 11, marginBottom: 4 }}>Problem Statement</p>
              <p style={{ color: C.text, fontSize: 14, lineHeight: 1.5 }}>{S(r.problemStatement)}</p>
            </Card>
            <Card style={{ padding: 16 }}>
              <p style={{ color: C.text2, fontSize: 11, marginBottom: 4 }}>Target Audience</p>
              <p style={{ color: C.text, fontSize: 14 }}>{S(r.targetAudience)}</p>
            </Card>
          </div>
        </div>

        {/* Clarity score improvement hint */}
        {(r.clarityScore as number) < 90 && (
          <div style={{
            background: 'rgba(124,111,247,0.08)', border: '1px solid rgba(124,111,247,0.2)',
            borderRadius: 12, padding: '14px 18px', marginBottom: 16, fontSize: 14,
          }}>
            <span style={{ color: C.primary, fontWeight: 600 }}>💡 To reach 90+: </span>
            <span style={{ color: C.text2 }}>
              {String(r.howToImproveScore || 'Narrow your target segment to a more specific sub-group and quantify the problem.')}
            </span>
          </div>
        )}

        {/* Flags */}
        <div className="r-grid-3" style={{ marginBottom: 16 }}>
          {Array.isArray(r.riskFlags) && r.riskFlags.length > 0 && (
            <Card style={{ background: `${C.amber}0d`, border: `1px solid ${C.amber}30`, padding: 16 }}>
              <p style={{ color: C.amber, fontSize: 11, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Risk Flags</p>
              {SA(r.riskFlags).map((f, i) => <div key={i} style={{ color: C.text2, fontSize: 12, marginBottom: 6 }}>⚠ {f}</div>)}
            </Card>
          )}
          {Array.isArray(r.strengthsOfIdea) && (
            <Card style={{ background: `${C.green}0d`, border: `1px solid ${C.green}30`, padding: 16 }}>
              <p style={{ color: C.green, fontSize: 11, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Strengths</p>
              {SA(r.strengthsOfIdea).map((s, i) => <div key={i} style={{ color: C.text2, fontSize: 12, marginBottom: 6 }}>✓ {s}</div>)}
            </Card>
          )}
          {Array.isArray(r.keyAssumptions) && (
            <Card style={{ background: `${C.primary}0d`, border: `1px solid ${C.primary}30`, padding: 16 }}>
              <p style={{ color: '#818cf8', fontSize: 11, fontWeight: 700, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Key Assumptions</p>
              {SA(r.keyAssumptions).map((a, i) => <div key={i} style={{ color: C.text2, fontSize: 12, marginBottom: 6 }}>? {a}</div>)}
            </Card>
          )}
        </div>

        {!!r.nextBestQuestion && (
          <div style={{ background: `${C.primary}0d`, border: `1px solid ${C.primary}30`, borderRadius: 12, padding: 18, marginBottom: 24 }}>
            <p style={{ color: C.primary, fontSize: 11, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Most Important Question to Answer</p>
            <p style={{ color: C.text2, fontSize: 14 }}>{S(r.nextBestQuestion)}</p>
          </div>
        )}

        <ErrMsg msg={advanceError} onDismiss={clearAdvanceError} />
        <div style={{ display: 'flex', gap: 12 }}>
          <GhostBtn onClick={() => setResult(null)}>← Edit Idea</GhostBtn>
          <button
            onClick={() => advance((p) => !p.idea?.aiOutput ? 'Idea analysis not saved. Try again.' : null)}
            disabled={advancing}
            style={{ flex: 2, background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`, border: 'none', borderRadius: 12, color: '#fff', fontSize: 16, fontWeight: 700, padding: '14px 0', cursor: advancing ? 'not-allowed' : 'pointer', opacity: advancing ? 0.7 : 1 }}
          >
            {advancing ? 'Saving…' : 'Run Reality Check →'}
          </button>
        </div>
      </div>
    );
  }

  // ── Sub-screen 2a: "Reading your idea..." micro-animation ──
  if (readingIdea) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '80px 24px', textAlign: 'center', animation: 'fadeUp 0.3s ease',
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', marginBottom: 32,
          background: `conic-gradient(from 0deg, ${C.primary}, ${C.accent}, rgba(124,111,247,0.1), ${C.primary})`,
          animation: 'spin 1.2s linear infinite',
          position: 'relative',
        }}>
          <div style={{ width: 48, height: 48, background: C.bg, borderRadius: '50%', position: 'absolute', top: 8, left: 8 }} />
        </div>
        <p style={{
          fontSize: 20, fontWeight: 700,
          background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          Reading your idea...
        </p>
      </div>
    );
  }

  // ── Sub-screen 2a: Just the idea textarea ──
  if (subScreen === 'a') {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', animation: 'fadeUp 0.35s ease', textAlign: 'center' }}>
        <h2 style={{ fontSize: 26, fontWeight: 800, color: C.text, marginBottom: 8 }}>What's your idea?</h2>
        <p style={{ color: C.text2, fontSize: 14, marginBottom: 28, lineHeight: 1.6 }}>
          Describe it in plain English. One sentence or ten — whatever feels natural.
        </p>
        <textarea
          value={rawIdea}
          onChange={(e) => setRawIdea(e.target.value)}
          placeholder="e.g. An app that helps freelancers track their invoices and get paid faster..."
          style={{
            width: '100%', background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 14, color: C.text, fontSize: 15, padding: 18,
            lineHeight: 1.6, resize: 'none', outline: 'none', minHeight: 180,
            boxSizing: 'border-box', fontFamily: 'inherit', textAlign: 'left',
          }}
          maxLength={1000}
        />
        <div style={{ textAlign: 'right', marginTop: 6, minHeight: 20 }}>
          {rawIdea.length > 800 && (
            <span style={{ color: C.green, fontSize: 13 }}>✓ That's plenty of detail</span>
          )}
        </div>
        <GradientBtn
          onClick={analyseAndTransition}
          disabled={rawIdea.trim().length < 10 || readingIdea}
          style={{ marginTop: 20 }}
        >
          Analyse My Idea →
        </GradientBtn>
      </div>
    );
  }

  // ── Sub-screen 2b: The 5 deeper questions (pre-filled) ──
  return (
    <div style={{ maxWidth: 640, margin: '0 auto', animation: 'fadeUp 0.35s ease' }}>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 8 }}>Help us go deeper</h2>
        <p style={{ color: C.text2, fontSize: 14, lineHeight: 1.6 }}>
          We've pre-filled these from your idea. Edit anything that's off.
        </p>
      </div>

      {/* Show raw idea as context */}
      <div style={{
        background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 12,
        padding: '12px 16px', marginBottom: 20, fontSize: 13, color: C.text2, lineHeight: 1.5,
      }}>
        <span style={{ color: C.text3, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>Your idea: </span>
        {rawIdea.length > 120 ? rawIdea.slice(0, 120) + '…' : rawIdea}
        <button onClick={() => setSubScreen('a')} style={{
          background: 'none', border: 'none', color: C.primary, fontSize: 12, cursor: 'pointer', marginLeft: 8,
        }}>← Edit</button>
      </div>

      {[
        { field: 'problemSolved', label: 'What specific problem does this solve?', placeholder: 'People spend 3 hours a week on...' },
        { field: 'whoSuffers', label: 'Who exactly suffers from this problem?', placeholder: 'Freelance designers aged 25-40 who...' },
        { field: 'currentSolution', label: "How do they solve it TODAY?", placeholder: 'They use Excel spreadsheets or...' },
        { field: 'promisedResult', label: 'What specific result do you promise?', placeholder: 'Users will get paid 5x faster...' },
        { field: 'budgetAndTime', label: "What's your budget and time horizon?", placeholder: 'I have 3 months and $2,000...' },
      ].map(({ field, label, placeholder }) => (
        <div key={field} style={{ marginBottom: 14 }} {...(fieldErrors[field] ? { 'data-field-error': true } : {})}>
          <label style={{ color: C.text2, fontSize: 12, display: 'block', marginBottom: 5 }}>{label}</label>
          <input
            type="text"
            value={(answers as Record<string, string>)[field]}
            onChange={(e) => {
              setAnswers({ ...answers, [field]: e.target.value });
              if (fieldErrors[field] && e.target.value.trim()) {
                setFieldErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
              }
            }}
            placeholder={placeholder}
            style={{
              width: '100%', background: flashFields.has(field) ? 'rgba(124,111,247,0.15)' : C.bg,
              border: `1px solid ${fieldErrors[field] ? C.danger : flashFields.has(field) ? 'rgba(124,111,247,0.5)' : C.border}`,
              borderRadius: 10, color: C.text, fontSize: 13, padding: '9px 12px',
              outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
              transition: 'background 0.6s ease, border-color 0.6s ease',
            }}
          />
          {fieldErrors[field] && (
            <p style={{ color: C.danger, fontSize: 12, marginTop: 4 }}>{fieldErrors[field]}</p>
          )}
        </div>
      ))}

      {/* FIX: #7 — retry button on step error */}
      <ErrMsg msg={error} onRetry={submit} />
      {toast && (
        <div style={{
          background: 'rgba(124,111,247,0.1)', border: '1px solid rgba(124,111,247,0.3)',
          borderRadius: 10, padding: '10px 16px', marginBottom: 12,
          color: C.primary, fontSize: 13, fontWeight: 500,
          animation: 'fadeUp 0.3s ease',
        }}>
          {toast}
        </div>
      )}
      <GradientBtn onClick={() => {
        // Validate all fields before submitting
        const errs: Record<string, string> = {};
        if (!rawIdea.trim() || rawIdea.trim().length < 10) errs.rawIdea = 'Please describe your idea (at least 10 characters)';
        if (!answers.problemSolved.trim()) errs.problemSolved = 'Please describe the problem your idea solves';
        if (!answers.whoSuffers.trim()) errs.whoSuffers = 'Please describe who suffers from this problem';
        if (!answers.currentSolution.trim()) errs.currentSolution = 'Please describe how people solve this today';
        if (!answers.promisedResult.trim()) errs.promisedResult = 'Please describe the result you promise';
        if (!answers.budgetAndTime.trim()) errs.budgetAndTime = 'Please describe your budget and time horizon to continue';
        setFieldErrors(errs);
        if (Object.keys(errs).length > 0) {
          const firstErrorEl = document.querySelector('[data-field-error]');
          firstErrorEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        submit();
      }} disabled={loading} style={{ marginTop: 24 }}>
        Structure My Idea →<CreditCostLabel step={1} isFirst={pipeline.isFirstPipeline} />
      </GradientBtn>
      {creditModal && <InsufficientCreditsModal action="PIPELINE_IDEA" required={creditModal.required} available={creditModal.available} onClose={() => { setCreditModal(null); fetchBalance(); }} />}
    </div>
  );
}

// ── STEP 2: Reality Check ─────────────────────────────────────

const REALITY_STEPS = [
  'Scanning competitor landscape…',
  'Sizing your target market…',
  'Checking timing signals…',
  'Scoring monetization potential…',
  'Assessing execution risk…',
];

function Step2Reality({ pipeline, onDone }: { pipeline: Pipeline; onDone: (updated: Pipeline) => void }) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<Record<string, unknown> | null>(
    pipeline.realityCheck?.aiOutput ? pipeline.realityCheck.aiOutput as Record<string, unknown> : null
  );
  const [creditModal, setCreditModal] = useState<{ required: number; available: number } | null>(null);
  const { optimisticDeduct, optimisticRestore, fetchBalance } = useCreditsStore();
  const { advancing, advanceError, clearAdvanceError, advance } = useContinue(pipeline.id, onDone);
  const { steps: loadingStepsData, progress: loadingProgress, finish } = useLoadingSteps(REALITY_STEPS, running);
  const autoTriggered = useRef(false);
  const loadingStartRef = useRef(0);

  const run = async () => {
    setRunning(true);
    setError('');
    loadingStartRef.current = Date.now();
    // FIX: #1 — optimistic credit deduct before AI call, restore on failure
    const COST = CREDIT_COSTS.PIPELINE_REALITY;
    let restored = false;
    optimisticDeduct(COST);
    const MAX_RETRIES = 1;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) await new Promise(r => setTimeout(r, 1500 * attempt));
        console.log('[Step2] Running reality check...');
        const { data } = await aiApi.post(`/pipeline/${pipeline.id}/reality-check`, {});
        console.log('[Step2] Reality check response:', data);
        const aiOutput = data?.data?.pipeline?.realityCheck?.aiOutput;
        if (!aiOutput?.verdict) throw new Error('Analysis incomplete — missing verdict. Please try again.');
        finish();
        // Minimum 4-second loading screen
        const elapsed = Date.now() - loadingStartRef.current;
        const remaining = Math.max(0, 4000 - elapsed);
        setTimeout(() => { setResult(aiOutput); setRunning(false); }, remaining + 600);
        return;
      } catch (err: any) {
        console.error('[Step2] Reality check error:', err);
        const is5xx = err?.response?.status >= 500;
        const is429 = err?.response?.status === 429;
        const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout');
        if ((is5xx || is429 || isTimeout) && attempt < MAX_RETRIES) { await new Promise(r => setTimeout(r, 2000 * (attempt + 1))); continue; }
        // FIX: #1 — restore credits on failure
        if (!restored) { optimisticRestore(COST); restored = true; }
        if (err?.response?.status === 402) {
          setCreditModal({ required: err.response.data.required, available: err.response.data.available });
        } else {
          setError(is5xx || is429 ? 'High demand right now — please wait 30 seconds and try again. Your credits were not deducted.' : classifyApiError(err));
        }
        fetchBalance();
        setRunning(false);
        return;
      }
    }
  };

  // Auto-trigger reality check when entering this step
  useEffect(() => {
    if (!result && !running && !autoTriggered.current) {
      autoTriggered.current = true;
      run();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (running) {
    return <LoadingCinematic stage="Running your reality check..." steps={loadingStepsData} progress={loadingProgress} subtitle="Searching live market data" creditReassurance footerText="This usually takes 10–20 seconds..." />;
  }

  if (!result) {
    return (
      <div style={{ maxWidth: 520, margin: '0 auto', textAlign: 'center', animation: 'fadeUp 0.4s ease' }}>
        <div style={{ fontSize: 56, marginBottom: 20, animation: 'float 3s ease-in-out infinite' }}>🔬</div>
        <h2 style={{ fontSize: 26, fontWeight: 800, color: C.text, marginBottom: 12 }}>Brutal Reality Check</h2>
        <p style={{ color: C.text2, fontSize: 14, lineHeight: 1.7, marginBottom: 32 }}>
          We'll scan real market data — keywords, trends, competitors, sentiment — and give you a blunt verdict.
        </p>
        {/* FIX: #7 — retry button on step error */}
        <ErrMsg msg={error} onRetry={run} />
        <GradientBtn onClick={run}>🔍 Run Reality Check<CreditCostLabel step={2} isFirst={pipeline.isFirstPipeline} /></GradientBtn>
        {creditModal && <InsufficientCreditsModal action="PIPELINE_REALITY" required={creditModal.required} available={creditModal.available} onClose={() => { setCreditModal(null); fetchBalance(); }} />}
      </div>
    );
  }

  const r = result;
  const rawVerdict = S(r.verdict) ?? 'NICHE';
  const verdictMap: Record<string, { label: string; icon: string; color: string; explainer?: string }> = {
    GO: { label: 'BUILD', icon: '✅', color: C.green },
    BUILD: { label: 'BUILD', icon: '✅', color: C.green },
    NICHE: { label: 'NICHE DOWN', icon: '🎯', color: C.amber, explainer: 'Real demand exists — but the space is crowded. Your path to winning is specificity.' },
    REWRITE: { label: 'PIVOT', icon: '🔄', color: '#f97316' },
    PIVOT: { label: 'PIVOT', icon: '🔄', color: '#f97316' },
    DROP: { label: 'KILL', icon: '⛔', color: C.danger },
    KILL: { label: 'KILL', icon: '⛔', color: C.danger },
  };
  const vm = verdictMap[rawVerdict] ?? { label: rawVerdict, icon: '🔄', color: C.text2 };
  const verdictColor = vm.color;
  const scores = (r.scores as Record<string, number>) ?? {};

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', animation: 'fadeUp 0.5s ease' }}>
      {/* Verdict stamp */}
      <div style={{
        background: `${verdictColor}0d`,
        border: `1px solid ${verdictColor}40`,
        borderRadius: 20, padding: '36px 40px',
        textAlign: 'center', marginBottom: 20,
      }}>
        <div style={{ color: C.text2, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>The Verdict</div>
        <div style={{
          fontSize: 68, fontWeight: 900, color: verdictColor, letterSpacing: -2, lineHeight: 1,
          marginBottom: 16, animation: 'verdict-stamp 0.6s ease both',
        }}>
          {vm.icon} {vm.label}
        </div>
        {vm.explainer && (
          <p style={{ color: verdictColor, fontSize: 14, fontWeight: 600, lineHeight: 1.5, marginBottom: 12, maxWidth: 560, margin: '0 auto 12px' }}>
            {vm.explainer}
          </p>
        )}
        <p style={{ color: C.text2, fontSize: 15, lineHeight: 1.7, maxWidth: 560, margin: '0 auto' }}>{S(r.verdictReasoning)}</p>
      </div>

      {/* Scores */}
      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ color: C.text, fontSize: 15, fontWeight: 700, marginBottom: 20 }}>Score Breakdown</h3>
        {Object.entries(scores).map(([key, score]) => (
          <ScoreBar key={key} label={key.replace(/([A-Z])/g, ' $1').trim()} score={score}
            color={score >= 70 ? C.green : score >= 50 ? C.amber : C.danger} animated />
        ))}
      </Card>

      {/* Killer insights */}
      {Array.isArray(r.killerInsights) && (
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ color: C.text, fontSize: 15, fontWeight: 700, marginBottom: 16 }}>💡 Killer Insights</h3>
          {SA(r.killerInsights).map((ins, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
              <span style={{ color: C.amber, flexShrink: 0 }}>→</span>
              <span style={{ color: C.text2, fontSize: 14, lineHeight: 1.5 }}>{ins}</span>
            </div>
          ))}
        </Card>
      )}

      {/* Threat / Opportunity */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
        <Card style={{ background: `${C.danger}0d`, border: `1px solid ${C.danger}30`, padding: 20 }}>
          <p style={{ color: C.danger, fontSize: 11, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>⚠ Biggest Threat</p>
          <p style={{ color: C.text2, fontSize: 14, lineHeight: 1.5 }}>{S(r.biggestThreat)}</p>
        </Card>
        <Card style={{ background: `${C.green}0d`, border: `1px solid ${C.green}30`, padding: 20 }}>
          <p style={{ color: C.green, fontSize: 11, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>🚀 Biggest Opportunity</p>
          <p style={{ color: C.text2, fontSize: 14, lineHeight: 1.5 }}>{S(r.biggestOpportunity)}</p>
        </Card>
      </div>

      {/* Course lesson link */}
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <a href="/course" style={{ color: C.primary, fontSize: 12, textDecoration: 'none' }}>→ Learn more about the problem-solution framework</a>
      </div>

      {/* CTAs */}
      <ErrMsg msg={advanceError} onDismiss={clearAdvanceError} />
      <div style={{ display: 'flex', gap: 12 }}>
        {(rawVerdict === 'REWRITE' || rawVerdict === 'PIVOT') && (
          <button
            onClick={async () => {
              // Go back to step 1 (Idea) — manually set currentStep override
              console.log('[Step2] PIVOT — going back to idea step');
              advance((p) => { p.currentStep = 1; return null; });
            }}
            disabled={advancing}
            style={{ flex: 1, background: C.amber, border: 'none', borderRadius: 12, color: '#000', fontSize: 14, fontWeight: 700, padding: '14px 0', cursor: advancing ? 'not-allowed' : 'pointer', opacity: advancing ? 0.7 : 1 }}>
            {advancing ? '…' : '← Edit Idea'}
          </button>
        )}
        {rawVerdict !== 'DROP' && rawVerdict !== 'KILL' && (
          <button
            onClick={() => advance((p) => !p.realityCheck?.aiOutput ? 'Reality check not saved. Try again.' : null)}
            disabled={advancing}
            style={{ flex: 2, background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`, border: 'none', borderRadius: 12, color: '#fff', fontSize: 15, fontWeight: 700, padding: '14px 0', cursor: advancing ? 'not-allowed' : 'pointer', opacity: advancing ? 0.7 : 1 }}>
            {advancing ? 'Saving…' : (rawVerdict === 'GO' || rawVerdict === 'BUILD') ? '✅ Define My Positioning →' : rawVerdict === 'NICHE' ? '🎯 Find My Niche →' : '→ Continue to Positioning'}
          </button>
        )}
        {(rawVerdict === 'DROP' || rawVerdict === 'KILL') && (
          <button
            onClick={() => advance()}
            disabled={advancing}
            style={{ flex: 1, background: `${C.danger}20`, border: `1px solid ${C.danger}`, borderRadius: 12, color: C.danger, fontSize: 14, fontWeight: 700, padding: '14px 0', cursor: advancing ? 'not-allowed' : 'pointer', opacity: advancing ? 0.7 : 1 }}>
            {advancing ? '…' : 'Move to Incubator'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── STEP 3: Strategic Positioning ────────────────────────────

interface PricingSuggestion {
  tier: string; price: number; period: string; displayPrice: string;
  reasoning: string; bestFor: string; expectedConversionRate: string;
  revenueAt50Users: number; revenueAt100Users: number;
}

/** Normalize backend pricing suggestions to PricingSuggestion shape */
function normalizePricingSuggestions(raw: any[]): PricingSuggestion[] {
  const tiers = ['entry', 'recommended', 'premium'];
  return raw.slice(0, 3).map((s: any, i: number) => {
    // Parse numeric price from string like "$19/mo" or "$49"
    const priceNum = parseFloat(String(s.price || s.displayPrice || '0').replace(/[^0-9.]/g, '')) || 0;
    return {
      tier: s.tier || s.label?.toLowerCase() || tiers[i] || 'entry',
      price: s.price != null && typeof s.price === 'number' ? s.price : priceNum,
      period: s.period || 'mo',
      displayPrice: s.displayPrice || s.price || `$${priceNum}`,
      reasoning: s.reasoning || s.bestFor || '',
      bestFor: s.bestFor || s.reasoning || '',
      expectedConversionRate: s.expectedConversionRate || '–',
      revenueAt50Users: s.revenueAt50Users || Math.round(priceNum * 50 * 0.03),
      revenueAt100Users: s.revenueAt100Users || Math.round(priceNum * 100 * 0.03),
    };
  });
}

function Step3Positioning({ pipeline, onDone }: { pipeline: Pipeline; onDone: (updated: Pipeline) => void }) {
  const [options, setOptions] = useState<{
    suggestedNiches: Record<string, unknown>[];
    differentiationAngles: Record<string, unknown>[];
    monetizationModels: Record<string, unknown>[];
  } | null>(
    pipeline.positioning?.suggestedNiches?.length ? {
      suggestedNiches: pipeline.positioning.suggestedNiches,
      differentiationAngles: pipeline.positioning.differentiationAngles ?? [],
      monetizationModels: pipeline.positioning.monetizationModels ?? [],
    } : null
  );
  const [choices, setChoices] = useState({
    selectedNiche: pipeline.positioning?.userChoices?.selectedNiche ?? '',
    selectedDifferentiation: pipeline.positioning?.userChoices?.selectedDifferentiation ?? '',
    selectedMonetization: pipeline.positioning?.userChoices?.selectedMonetization ?? '',
  });
  const [launchPrice, setLaunchPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [blueprintLoading, setBlueprintLoading] = useState(false);
  const [result, setResult] = useState<Record<string, unknown> | null>(
    pipeline.positioning?.aiOutput ? pipeline.positioning.aiOutput as Record<string, unknown> : null
  );
  const [error, setError] = useState('');
  const [confetti, setConfetti] = useState(false);
  const [editingCard, setEditingCard] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [rewritingCard, setRewritingCard] = useState<string | null>(null);
  const [customNiches, setCustomNiches] = useState<Record<string, unknown>[]>([]);
  const [customAngles, setCustomAngles] = useState<Record<string, unknown>[]>([]);
  const [customMonetization, setCustomMonetization] = useState<Record<string, unknown>[]>([]);
  const [addingCustom, setAddingCustom] = useState<'niche' | 'angle' | 'monetization' | null>(null);
  const [newCustomName, setNewCustomName] = useState('');
  const [newCustomDesc, setNewCustomDesc] = useState('');
  const [editedNiches, setEditedNiches] = useState<Record<string, unknown>[] | null>(null);
  const [editedAngles, setEditedAngles] = useState<Record<string, unknown>[] | null>(null);
  const [editedMonetization, setEditedMonetization] = useState<Record<string, unknown>[] | null>(null);
  const [pricingSuggestions, setPricingSuggestions] = useState<PricingSuggestion[]>([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [selectedPricingTier, setSelectedPricingTier] = useState('');
  const [creditModal, setCreditModal] = useState<{ required: number; available: number } | null>(null);
  const { optimisticDeduct, optimisticRestore, fetchBalance } = useCreditsStore();
  const { advancing, advanceError, clearAdvanceError, advance } = useContinue(pipeline.id, onDone);
  const { steps: genSteps, progress: genProgress, finish: genFinish } = useLoadingSteps([
    'Analyzing your idea…', 'Mapping market niches…', 'Finding differentiation angles…', 'Modeling revenue streams…',
  ], loading);
  const { steps: bpSteps, progress: bpProgress, finish: bpFinish } = useLoadingSteps([
    'Processing your choices…', 'Building positioning statement…', 'Generating elevator pitch…', 'Creating persona…',
  ], blueprintLoading);
  const autoTriggered = useRef(false);

  const generateOptions = async () => {
    setLoading(true);
    setError('');
    // FIX: #1 — optimistic credit deduct before AI call, restore on failure
    const COST = CREDIT_COSTS.PIPELINE_POSITIONING;
    let restored = false;
    optimisticDeduct(COST);
    const MAX_RETRIES = 1;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) await new Promise(r => setTimeout(r, 1500 * attempt));
        console.log('[Step3] Generating positioning options...');
        const { data } = await aiApi.post(`/pipeline/${pipeline.id}/positioning`, {});
        console.log('[Step3] Positioning options response:', data);
        const p = data?.data?.pipeline as Pipeline;
        if (!p?.positioning?.suggestedNiches?.length) throw new Error('Positioning options incomplete. Try again.');
        genFinish();
        setTimeout(() => {
          setOptions({
            suggestedNiches: p.positioning?.suggestedNiches ?? [],
            differentiationAngles: p.positioning?.differentiationAngles ?? [],
            monetizationModels: p.positioning?.monetizationModels ?? [],
          });
          setLoading(false);
        }, 400);
        return;
      } catch (err: any) {
        console.error('[Step3] Positioning options error:', err);
        const is5xx = err?.response?.status >= 500;
        const is429 = err?.response?.status === 429;
        const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout');
        if ((is5xx || is429 || isTimeout) && attempt < MAX_RETRIES) { await new Promise(r => setTimeout(r, 2000 * (attempt + 1))); continue; }
        // FIX: #1 — restore credits on failure
        if (!restored) { optimisticRestore(COST); restored = true; }
        if (err?.response?.status === 402) {
          setCreditModal({ required: err.response.data.required, available: err.response.data.available });
        } else {
          setError(is5xx || is429 ? 'High demand right now — please wait 30 seconds and try again. Your credits were not deducted.' : classifyApiError(err));
        }
        fetchBalance();
        setLoading(false);
        return;
      }
    }
  };

  const chooseAndGenerate = async () => {
    setBlueprintLoading(true);
    setError('');
    // FIX: #1 — optimistic credit deduct before AI call, restore on failure
    const COST2 = CREDIT_COSTS.PIPELINE_POSITIONING;
    let restored2 = false;
    optimisticDeduct(COST2);
    try {
      const choicesPayload = { ...choices, ...(launchPrice ? { launchPrice: Number(launchPrice) } : {}) };
      console.log('[Step3] Generating blueprint with choices:', choicesPayload);
      const { data } = await aiApi.post(`/pipeline/${pipeline.id}/positioning/choose`, choicesPayload);
      console.log('[Step3] Blueprint response:', data);
      const aiOutput = data?.data?.pipeline?.positioning?.aiOutput;
      if (!aiOutput?.positioningStatement) throw new Error('Blueprint incomplete — missing positioning statement. Try again.');
      bpFinish();
      // Fix 6: Auto-update pipeline title from AI suggested product name
      const suggestedName = String(aiOutput.suggestedProductName ?? '').trim();
      if (suggestedName) {
        api.patch(`/pipeline/${pipeline.id}`, { title: suggestedName }).catch(() => {});
        document.title = `${suggestedName} — StartupScore`;
      }
      setTimeout(() => {
        setResult(aiOutput);
        setBlueprintLoading(false);
        setConfetti(true);
        setTimeout(() => setConfetti(false), 3500);
        // B2: Enrich linked startup with positioning data (fire-and-forget)
        api.get(`/pipeline/${pipeline.id}`).then(({ data: pd }) => {
          const linkedId = (pd?.data?.pipeline as Pipeline)?.linkedStartupId;
          if (!linkedId) return;
          const uvp = (S(aiOutput.positioningStatement) ?? '').slice(0, 500);
          const segment = choices.selectedNiche || inferCategoryFromIdea(pipeline);
          api.put(`/startups/${linkedId}`, {
            ...(uvp ? { uniqueValueProposition: uvp } : {}),
            targetSegment: segment,
          }).catch(() => {});
        }).catch(() => {});
      }, 400);
    } catch (err: any) {
      console.error('[Step3] Blueprint error:', err);
      // FIX: #1 — restore credits on failure
      if (!restored2) { optimisticRestore(COST2); restored2 = true; }
      if (err?.response?.status === 402) {
        const d = err.response.data;
        setCreditModal({ required: d.required, available: d.available });
      } else {
        setError(classifyApiError(err));
      }
      fetchBalance();
      setBlueprintLoading(false);
    }
  };

  const rewriteOption = async (
    column: 'niche' | 'angle' | 'monetization',
    index: number,
    currentOption: Record<string, unknown>
  ) => {
    const cardKey = `${column}-${index}`;
    setRewritingCard(cardKey);
    try {
      const { data } = await api.post('/pipeline/rewrite-option', {
        column,
        currentOption,
        startupIdea: pipeline.idea?.aiOutput?.ideaSnapshot ?? '',
        founderType: pipeline.userProfile?.aiOutput?.founderType ?? '',
      });
      const rewritten = data?.data?.rewritten as Record<string, unknown>;
      if (column === 'niche') {
        const base = editedNiches ?? options?.suggestedNiches ?? [];
        const updated = [...base]; updated[index] = rewritten;
        setEditedNiches(updated);
      } else if (column === 'angle') {
        const base = editedAngles ?? options?.differentiationAngles ?? [];
        const updated = [...base]; updated[index] = rewritten;
        setEditedAngles(updated);
      } else {
        const base = editedMonetization ?? options?.monetizationModels ?? [];
        const updated = [...base]; updated[index] = rewritten;
        setEditedMonetization(updated);
      }
    } catch { setError('Could not rewrite this option — please try again.'); }
    finally { setRewritingCard(null); }
  };

  const saveEdit = (column: 'niche' | 'angle' | 'monetization', index: number) => {
    const key = `${column}-${index}`;
    const fields = editValues;
    if (column === 'niche') {
      const base = editedNiches ?? options?.suggestedNiches ?? [];
      const updated = [...base];
      updated[index] = { ...updated[index], ...Object.fromEntries(Object.entries(fields).filter(([k]) => k.startsWith(key + '-')).map(([k, v]) => [k.replace(key + '-', ''), v])) };
      setEditedNiches(updated);
    } else if (column === 'angle') {
      const base = editedAngles ?? options?.differentiationAngles ?? [];
      const updated = [...base];
      updated[index] = { ...updated[index], ...Object.fromEntries(Object.entries(fields).filter(([k]) => k.startsWith(key + '-')).map(([k, v]) => [k.replace(key + '-', ''), v])) };
      setEditedAngles(updated);
    } else {
      const base = editedMonetization ?? options?.monetizationModels ?? [];
      const updated = [...base];
      updated[index] = { ...updated[index], ...Object.fromEntries(Object.entries(fields).filter(([k]) => k.startsWith(key + '-')).map(([k, v]) => [k.replace(key + '-', ''), v])) };
      setEditedMonetization(updated);
    }
    setEditingCard(null);
    setEditValues({});
  };

  const startEdit = (column: 'niche' | 'angle' | 'monetization', index: number, option: Record<string, unknown>) => {
    const key = `${column}-${index}`;
    setEditingCard(key);
    const vals: Record<string, string> = {};
    Object.entries(option).forEach(([k, v]) => { vals[`${key}-${k}`] = String(v ?? ''); });
    setEditValues(vals);
  };

  const [pricingError, setPricingError] = useState('');

  const fetchPricingSuggestions = async () => {
    setPricingLoading(true);
    setPricingError('');
    try {
      const { data } = await api.post('/pipeline/pricing-suggestions', {
        ideaSnapshot: pipeline.idea?.aiOutput?.ideaSnapshot ?? '',
        targetAudience: pipeline.idea?.aiOutput?.targetAudience ?? '',
        monetizationModel: choices.selectedMonetization,
        founderType: pipeline.userProfile?.aiOutput?.founderType ?? '',
      });
      const raw = data?.data?.suggestions ?? [];
      setPricingSuggestions(normalizePricingSuggestions(raw));
    } catch {
      setPricingError('Could not load pricing suggestions. Try again.');
    } finally {
      setPricingLoading(false);
    }
  };

  // Auto-trigger positioning generation when entering this step
  useEffect(() => {
    if (!options && !loading && !autoTriggered.current) {
      autoTriggered.current = true;
      generateOptions();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <LoadingCinematic stage="Generating your positioning options..." steps={genSteps} progress={genProgress} subtitle="Finding your strategic sweet spot" creditReassurance />;
  if (blueprintLoading) return <LoadingCinematic stage="Building your positioning blueprint..." steps={bpSteps} progress={bpProgress} subtitle="Crafting your unique position" creditReassurance />;

  if (result) {
    const r = result;
    const persona = (r.earlyAdopterPersona as Record<string, string>) ?? {};
    return (
      <div style={{ maxWidth: 760, margin: '0 auto', animation: 'fadeUp 0.4s ease' }}>
        <Confetti active={confetti} />

        {/* Persona */}
        <Card style={{ border: `1px solid ${C.primary}40`, marginBottom: 16 }}>
          <h3 style={{ color: C.primary, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 20 }}>Early Adopter Persona</h3>
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
            <div style={{
              width: 60, height: 60, borderRadius: '50%',
              background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`,
              flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26,
            }}>
              {persona.name?.[0] ?? '👤'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 4 }}>{persona.name}</div>
              <div style={{ color: C.text2, fontSize: 13, marginBottom: 14 }}>{persona.age} · {persona.job}</div>
              {persona.quote && (
                <div style={{ background: C.bg, borderRadius: 10, padding: 14, marginBottom: 14 }}>
                  <p style={{ color: C.text2, fontSize: 13, fontStyle: 'italic', lineHeight: 1.6 }}>"{persona.quote}"</p>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {persona.frustration && <div><p style={{ color: C.text3, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>Frustration</p><p style={{ color: C.text2, fontSize: 12 }}>{persona.frustration}</p></div>}
                {persona.goal && <div><p style={{ color: C.text3, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>Goal</p><p style={{ color: C.text2, fontSize: 12 }}>{persona.goal}</p></div>}
                {persona.whereToFind && <div style={{ gridColumn: 'span 2' }}><p style={{ color: C.text3, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>Where to find them</p><p style={{ color: C.green, fontSize: 12 }}>{persona.whereToFind}</p></div>}
              </div>
            </div>
          </div>
        </Card>

        {/* Positioning Statement */}
        {!!r.positioningStatement && (
          <Card style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>Positioning Statement</h3>
              <CopyBtn text={S(r.positioningStatement)} />
            </div>
            <p style={{ color: C.text, fontSize: 16, lineHeight: 1.8, fontStyle: 'italic' }}>{S(r.positioningStatement)}</p>
          </Card>
        )}

        {/* Elevator Pitch */}
        {!!r.elevatorPitch && (
          <Card style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>Elevator Pitch</h3>
              <CopyBtn text={S(r.elevatorPitch)} />
            </div>
            <p style={{ color: C.text2, fontSize: 14, lineHeight: 1.7 }}>{S(r.elevatorPitch)}</p>
          </Card>
        )}

        {!!r.uniqueMechanism && (
          <div style={{ background: `${C.primary}0d`, border: `1px solid ${C.primary}30`, borderRadius: 12, padding: 18, marginBottom: 24 }}>
            <p style={{ color: '#818cf8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Unique Mechanism</p>
            <p style={{ color: C.text2, fontSize: 14 }}>{S(r.uniqueMechanism)}</p>
          </div>
        )}

        <ErrMsg msg={advanceError} onDismiss={clearAdvanceError} />
        <GradientBtn
          onClick={() => advance((p) => !p.positioning?.aiOutput ? 'Positioning not saved. Try again.' : null)}
          disabled={advancing}
        >
          {advancing ? 'Saving…' : '→ Build My Validation Plan'}
        </GradientBtn>
      </div>
    );
  }

  if (!options) {
    return (
      <div style={{ maxWidth: 500, margin: '0 auto', textAlign: 'center', animation: 'fadeUp 0.4s ease' }}>
        <div style={{ fontSize: 52, marginBottom: 20, animation: 'float 3s ease-in-out infinite' }}>🎯</div>
        <h2 style={{ fontSize: 26, fontWeight: 800, color: C.text, marginBottom: 12 }}>Strategic Narrowing</h2>
        <p style={{ color: C.text2, fontSize: 14, lineHeight: 1.7, marginBottom: 32 }}>
          Claude will generate 3 niches, 3 differentiation angles, and 3 revenue models. You pick one of each.
        </p>
        {/* FIX: #7 — retry button on step error */}
        <ErrMsg msg={error} onRetry={generateOptions} />
        <GradientBtn onClick={generateOptions}>→ Generate My Options</GradientBtn>
        {creditModal && <InsufficientCreditsModal action="PIPELINE_POSITIONING" required={creditModal.required} available={creditModal.available} onClose={() => { setCreditModal(null); fetchBalance(); }} />}
      </div>
    );
  }

  const allChosen = choices.selectedNiche && choices.selectedDifferentiation && choices.selectedMonetization;

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto', animation: 'fadeUp 0.4s ease' }}>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 8 }}>Now let's narrow your focus.</h2>
      <p style={{ color: C.text2, fontSize: 14, marginBottom: 28 }}>Pick one from each column to define your positioning.</p>

      <div className="r-grid-3" style={{ gap: 18, marginBottom: 24 }}>
        {/* ── Niches ── */}
        <div>
          <h3 style={{ color: C.primary, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>🎯 Pick Your Niche</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(editedNiches ?? options.suggestedNiches).concat(customNiches).map((niche, i) => {
              const n = niche as Record<string, string>;
              const sel = choices.selectedNiche === n.niche;
              const cardKey = `niche-${i}`;
              const isEditing = editingCard === cardKey;
              const isRewriting = rewritingCard === cardKey;
              return (
                <div key={i} style={{ position: 'relative' }}>
                  <button onClick={() => !isEditing && setChoices({ ...choices, selectedNiche: n.niche })}
                    style={{
                      width: '100%',
                      background: isEditing ? 'rgba(124,111,247,0.08)' : sel ? 'rgba(249,115,22,0.08)' : C.surface,
                      border: sel && !isEditing ? '2px solid #f97316' : `${isEditing ? 2 : 1}px solid ${isEditing ? 'rgba(124,111,247,0.6)' : 'rgba(255,255,255,0.08)'}`,
                      borderRadius: 14, padding: 16, cursor: isEditing ? 'default' : 'pointer', textAlign: 'left',
                      transition: 'all 0.15s', boxShadow: sel && !isEditing ? '0 0 20px rgba(249,115,22,0.2)' : 'none',
                      position: 'relative',
                    }}>
                    {sel && !isEditing && (
                      <div style={{ position: 'absolute', top: -8, right: -8, width: 20, height: 20, borderRadius: '50%', background: '#f97316', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 700, zIndex: 3 }}>✓</div>
                    )}
                    {isEditing ? (
                      <div onClick={(e) => e.stopPropagation()}>
                        {(['niche', 'why', 'difficulty', 'potential'] as const).map((f) => (
                          <div key={f} style={{ marginBottom: 8 }}>
                            <div style={{ color: C.text3, fontSize: 10, textTransform: 'uppercase', marginBottom: 3 }}>{f}</div>
                            <input value={editValues[`${cardKey}-${f}`] ?? ''} onChange={(e) => setEditValues({ ...editValues, [`${cardKey}-${f}`]: e.target.value })}
                              style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.2)', color: C.text, fontSize: 13, padding: '4px 0', outline: 'none', boxSizing: 'border-box' }} />
                          </div>
                        ))}
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          <button onClick={() => saveEdit('niche', i)} style={{ flex: 1, background: C.primary, border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 700, padding: '7px 0', cursor: 'pointer' }}>Save ✓</button>
                          <button onClick={() => { setEditingCard(null); setEditValues({}); }} style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, color: C.text2, fontSize: 12, padding: '7px 12px', cursor: 'pointer' }}>✕</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ color: C.text, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{n.niche}</div>
                        <div style={{ color: C.text2, fontSize: 12, marginBottom: 8, lineHeight: 1.4 }}>{n.why}</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ background: C.surface2, borderRadius: 5, padding: '2px 7px', fontSize: 10, color: C.text2 }}>Difficulty: {n.difficulty}</span>
                          <span style={{ background: `${C.green}18`, borderRadius: 5, padding: '2px 7px', fontSize: 10, color: C.green }}>Potential: {n.potential}</span>
                        </div>
                        {n.firstCustomers && <div style={{ color: C.primary, fontSize: 11, marginTop: 8 }}>📍 {n.firstCustomers}</div>}
                      </>
                    )}
                  </button>
                  {/* Edit button */}
                  {!isEditing && (
                    <button onClick={(e) => { e.stopPropagation(); startEdit('niche', i, n); }}
                      style={{ position: 'absolute', top: 10, left: 10, width: 22, height: 22, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, color: C.text3, fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                      ✏️
                    </button>
                  )}
                  {/* Rewrite button */}
                  {!isEditing && (
                    <button onClick={(e) => { e.stopPropagation(); rewriteOption('niche', i, n); }}
                      title="AI: Rewrite this option"
                      style={{ position: 'absolute', top: 10, right: 10, width: 28, height: 28, borderRadius: '50%', background: isRewriting ? 'rgba(124,111,247,0.3)' : 'rgba(124,111,247,0.15)', border: `1px solid rgba(124,111,247,0.3)`, color: C.text, fontSize: 14, cursor: isRewriting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, transition: 'all 0.15s', animation: isRewriting ? 'spin 1s linear infinite' : 'none' }}>
                      ✨
                    </button>
                  )}
                </div>
              );
            })}
            {/* Add my own niche */}
            {customNiches.length < 2 && !addingCustom && (
              <button onClick={() => setAddingCustom('niche')}
                style={{ border: `1px dashed ${C.border}`, borderRadius: 14, padding: '14px 16px', background: 'transparent', color: C.text3, fontSize: 13, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>+</span> Add my own niche
              </button>
            )}
            {addingCustom === 'niche' && (
              <div style={{ background: C.surface, border: `1px solid ${C.primary}40`, borderRadius: 14, padding: 16 }}>
                <input placeholder="Niche name" value={newCustomName} onChange={(e) => setNewCustomName(e.target.value)}
                  style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, padding: '8px 12px', outline: 'none', boxSizing: 'border-box', marginBottom: 8 }} />
                <input placeholder="Why this niche?" value={newCustomDesc} onChange={(e) => setNewCustomDesc(e.target.value)}
                  style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, padding: '8px 12px', outline: 'none', boxSizing: 'border-box', marginBottom: 10 }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { if (newCustomName) { setCustomNiches([...customNiches, { niche: newCustomName, why: newCustomDesc, difficulty: 'Medium', potential: 'High' }]); setNewCustomName(''); setNewCustomDesc(''); setAddingCustom(null); } }}
                    style={{ flex: 1, background: C.primary, border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 700, padding: '8px 0', cursor: 'pointer' }}>Add ✓</button>
                  <button onClick={() => { setAddingCustom(null); setNewCustomName(''); setNewCustomDesc(''); }}
                    style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, color: C.text2, fontSize: 12, padding: '8px 12px', cursor: 'pointer' }}>✕</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Angles ── */}
        <div>
          <h3 style={{ color: C.accent, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>⚡ Pick Your Angle</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(editedAngles ?? options.differentiationAngles).concat(customAngles).map((angle, i) => {
              const a = angle as Record<string, string>;
              const sel = choices.selectedDifferentiation === a.angle;
              const cardKey = `angle-${i}`;
              const isEditing = editingCard === cardKey;
              const isRewriting = rewritingCard === cardKey;
              return (
                <div key={i} style={{ position: 'relative' }}>
                  <button onClick={() => !isEditing && setChoices({ ...choices, selectedDifferentiation: a.angle })}
                    style={{
                      width: '100%',
                      background: isEditing ? 'rgba(0,212,255,0.06)' : sel ? 'rgba(249,115,22,0.08)' : C.surface,
                      border: sel && !isEditing ? '2px solid #f97316' : `${isEditing ? 2 : 1}px solid ${isEditing ? 'rgba(0,212,255,0.5)' : 'rgba(255,255,255,0.08)'}`,
                      borderRadius: 14, padding: 16, cursor: isEditing ? 'default' : 'pointer', textAlign: 'left',
                      transition: 'all 0.15s', boxShadow: sel && !isEditing ? '0 0 20px rgba(249,115,22,0.2)' : 'none',
                      position: 'relative',
                    }}>
                    {sel && !isEditing && (
                      <div style={{ position: 'absolute', top: -8, right: -8, width: 20, height: 20, borderRadius: '50%', background: '#f97316', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 700, zIndex: 3 }}>✓</div>
                    )}
                    {isEditing ? (
                      <div onClick={(e) => e.stopPropagation()}>
                        {(['angle', 'description', 'examples', 'fit'] as const).map((f) => (
                          <div key={f} style={{ marginBottom: 8 }}>
                            <div style={{ color: C.text3, fontSize: 10, textTransform: 'uppercase', marginBottom: 3 }}>{f}</div>
                            <input value={editValues[`${cardKey}-${f}`] ?? ''} onChange={(e) => setEditValues({ ...editValues, [`${cardKey}-${f}`]: e.target.value })}
                              style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.2)', color: C.text, fontSize: 13, padding: '4px 0', outline: 'none', boxSizing: 'border-box' }} />
                          </div>
                        ))}
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          <button onClick={() => saveEdit('angle', i)} style={{ flex: 1, background: C.accent, border: 'none', borderRadius: 8, color: '#000', fontSize: 12, fontWeight: 700, padding: '7px 0', cursor: 'pointer' }}>Save ✓</button>
                          <button onClick={() => { setEditingCard(null); setEditValues({}); }} style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, color: C.text2, fontSize: 12, padding: '7px 12px', cursor: 'pointer' }}>✕</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ color: C.text, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{a.angle}</div>
                        <div style={{ color: C.text2, fontSize: 12, marginBottom: 6, lineHeight: 1.4 }}>{a.description}</div>
                        {a.examples && <div style={{ color: C.text2, fontSize: 11, marginBottom: 4 }}>e.g. {a.examples}</div>}
                        {a.fit && <div style={{ color: C.accent, fontSize: 11 }}>→ {a.fit}</div>}
                      </>
                    )}
                  </button>
                  {!isEditing && (
                    <button onClick={(e) => { e.stopPropagation(); startEdit('angle', i, a); }}
                      style={{ position: 'absolute', top: 10, left: 10, width: 22, height: 22, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, color: C.text3, fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                      ✏️
                    </button>
                  )}
                  {!isEditing && (
                    <button onClick={(e) => { e.stopPropagation(); rewriteOption('angle', i, a); }}
                      title="AI: Rewrite this option"
                      style={{ position: 'absolute', top: 10, right: 10, width: 28, height: 28, borderRadius: '50%', background: isRewriting ? 'rgba(0,212,255,0.3)' : 'rgba(0,212,255,0.1)', border: `1px solid rgba(0,212,255,0.3)`, color: C.text, fontSize: 14, cursor: isRewriting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, transition: 'all 0.15s', animation: isRewriting ? 'spin 1s linear infinite' : 'none' }}>
                      ✨
                    </button>
                  )}
                </div>
              );
            })}
            {customAngles.length < 2 && !addingCustom && (
              <button onClick={() => setAddingCustom('angle')}
                style={{ border: `1px dashed ${C.border}`, borderRadius: 14, padding: '14px 16px', background: 'transparent', color: C.text3, fontSize: 13, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>+</span> Add my own angle
              </button>
            )}
            {addingCustom === 'angle' && (
              <div style={{ background: C.surface, border: `1px solid ${C.accent}40`, borderRadius: 14, padding: 16 }}>
                <input placeholder="Angle name" value={newCustomName} onChange={(e) => setNewCustomName(e.target.value)}
                  style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, padding: '8px 12px', outline: 'none', boxSizing: 'border-box', marginBottom: 8 }} />
                <input placeholder="Description" value={newCustomDesc} onChange={(e) => setNewCustomDesc(e.target.value)}
                  style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, padding: '8px 12px', outline: 'none', boxSizing: 'border-box', marginBottom: 10 }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { if (newCustomName) { setCustomAngles([...customAngles, { angle: newCustomName, description: newCustomDesc }]); setNewCustomName(''); setNewCustomDesc(''); setAddingCustom(null); } }}
                    style={{ flex: 1, background: C.accent, border: 'none', borderRadius: 8, color: '#000', fontSize: 12, fontWeight: 700, padding: '8px 0', cursor: 'pointer' }}>Add ✓</button>
                  <button onClick={() => { setAddingCustom(null); setNewCustomName(''); setNewCustomDesc(''); }}
                    style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, color: C.text2, fontSize: 12, padding: '8px 12px', cursor: 'pointer' }}>✕</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Revenue models ── */}
        <div>
          <h3 style={{ color: C.green, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>💰 Pick Revenue Model</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(editedMonetization ?? options.monetizationModels).concat(customMonetization).map((model, i) => {
              const m = model as Record<string, string>;
              const sel = choices.selectedMonetization === m.model;
              const cardKey = `monetization-${i}`;
              const isEditing = editingCard === cardKey;
              const isRewriting = rewritingCard === cardKey;
              return (
                <div key={i} style={{ position: 'relative' }}>
                  <button onClick={() => !isEditing && setChoices({ ...choices, selectedMonetization: m.model })}
                    style={{
                      width: '100%',
                      background: isEditing ? 'rgba(0,230,118,0.06)' : sel ? 'rgba(249,115,22,0.08)' : C.surface,
                      border: sel && !isEditing ? '2px solid #f97316' : `${isEditing ? 2 : 1}px solid ${isEditing ? 'rgba(0,230,118,0.5)' : 'rgba(255,255,255,0.08)'}`,
                      borderRadius: 14, padding: 16, cursor: isEditing ? 'default' : 'pointer', textAlign: 'left',
                      transition: 'all 0.15s', boxShadow: sel && !isEditing ? '0 0 20px rgba(249,115,22,0.2)' : 'none',
                      position: 'relative',
                    }}>
                    {sel && !isEditing && (
                      <div style={{ position: 'absolute', top: -8, right: -8, width: 20, height: 20, borderRadius: '50%', background: '#f97316', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 700, zIndex: 3 }}>✓</div>
                    )}
                    {isEditing ? (
                      <div onClick={(e) => e.stopPropagation()}>
                        {(['model', 'description', 'timeToFirstRevenue', 'avgRevenue'] as const).map((f) => (
                          <div key={f} style={{ marginBottom: 8 }}>
                            <div style={{ color: C.text3, fontSize: 10, textTransform: 'uppercase', marginBottom: 3 }}>{f}</div>
                            <input value={editValues[`${cardKey}-${f}`] ?? ''} onChange={(e) => setEditValues({ ...editValues, [`${cardKey}-${f}`]: e.target.value })}
                              style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.2)', color: C.text, fontSize: 13, padding: '4px 0', outline: 'none', boxSizing: 'border-box' }} />
                          </div>
                        ))}
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          <button onClick={() => saveEdit('monetization', i)} style={{ flex: 1, background: C.green, border: 'none', borderRadius: 8, color: '#000', fontSize: 12, fontWeight: 700, padding: '7px 0', cursor: 'pointer' }}>Save ✓</button>
                          <button onClick={() => { setEditingCard(null); setEditValues({}); }} style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, color: C.text2, fontSize: 12, padding: '7px 12px', cursor: 'pointer' }}>✕</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ color: C.text, fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{m.model}</div>
                        <div style={{ color: C.text2, fontSize: 12, marginBottom: 6, lineHeight: 1.4 }}>{m.description}</div>
                        {m.timeToFirstRevenue && <div style={{ color: C.green, fontSize: 11, marginBottom: 3 }}>⏱ {m.timeToFirstRevenue}</div>}
                        {m.avgRevenue && <div style={{ color: C.text2, fontSize: 11 }}>~{m.avgRevenue}</div>}
                      </>
                    )}
                  </button>
                  {!isEditing && (
                    <button onClick={(e) => { e.stopPropagation(); startEdit('monetization', i, m); }}
                      style={{ position: 'absolute', top: 10, left: 10, width: 22, height: 22, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, color: C.text3, fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                      ✏️
                    </button>
                  )}
                  {!isEditing && (
                    <button onClick={(e) => { e.stopPropagation(); rewriteOption('monetization', i, m); }}
                      title="AI: Rewrite this option"
                      style={{ position: 'absolute', top: 10, right: 10, width: 28, height: 28, borderRadius: '50%', background: isRewriting ? 'rgba(0,230,118,0.3)' : 'rgba(0,230,118,0.1)', border: `1px solid rgba(0,230,118,0.3)`, color: C.text, fontSize: 14, cursor: isRewriting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, transition: 'all 0.15s', animation: isRewriting ? 'spin 1s linear infinite' : 'none' }}>
                      ✨
                    </button>
                  )}
                </div>
              );
            })}
            {customMonetization.length < 2 && !addingCustom && (
              <button onClick={() => setAddingCustom('monetization')}
                style={{ border: `1px dashed ${C.border}`, borderRadius: 14, padding: '14px 16px', background: 'transparent', color: C.text3, fontSize: 13, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>+</span> Add my own model
              </button>
            )}
            {addingCustom === 'monetization' && (
              <div style={{ background: C.surface, border: `1px solid ${C.green}40`, borderRadius: 14, padding: 16 }}>
                <input placeholder="Revenue model name" value={newCustomName} onChange={(e) => setNewCustomName(e.target.value)}
                  style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, padding: '8px 12px', outline: 'none', boxSizing: 'border-box', marginBottom: 8 }} />
                <input placeholder="Description" value={newCustomDesc} onChange={(e) => setNewCustomDesc(e.target.value)}
                  style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, padding: '8px 12px', outline: 'none', boxSizing: 'border-box', marginBottom: 10 }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { if (newCustomName) { setCustomMonetization([...customMonetization, { model: newCustomName, description: newCustomDesc }]); setNewCustomName(''); setNewCustomDesc(''); setAddingCustom(null); } }}
                    style={{ flex: 1, background: C.green, border: 'none', borderRadius: 8, color: '#000', fontSize: 12, fontWeight: 700, padding: '8px 0', cursor: 'pointer' }}>Add ✓</button>
                  <button onClick={() => { setAddingCustom(null); setNewCustomName(''); setNewCustomDesc(''); }}
                    style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, color: C.text2, fontSize: 12, padding: '8px 12px', cursor: 'pointer' }}>✕</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sticky selection summary bar */}
      <div style={{
        position: 'sticky', bottom: 0, left: 0, right: 0,
        background: 'rgba(5,5,16,0.95)', backdropFilter: 'blur(12px)',
        border: `1px solid ${allChosen ? 'rgba(249,115,22,0.4)' : C.border}`,
        borderRadius: 14, padding: '14px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, marginBottom: 24, zIndex: 10,
        transition: 'all 0.3s',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, flexWrap: 'wrap' }}>
          <span style={{ color: choices.selectedNiche ? '#f97316' : C.text3 }}>
            {choices.selectedNiche ? '✓ Niche' : '○ Niche'}
          </span>
          <span style={{ color: C.text3 }}>·</span>
          <span style={{ color: choices.selectedDifferentiation ? '#f97316' : C.text3 }}>
            {choices.selectedDifferentiation ? '✓ Angle' : '○ Angle'}
          </span>
          <span style={{ color: C.text3 }}>·</span>
          <span style={{ color: choices.selectedMonetization ? '#f97316' : C.text3 }}>
            {choices.selectedMonetization ? '✓ Revenue' : '○ Revenue'}
          </span>
        </div>
        <button
          onClick={allChosen ? chooseAndGenerate : undefined}
          disabled={!allChosen}
          style={{
            background: allChosen ? '#f97316' : 'rgba(255,255,255,0.06)',
            border: 'none',
            borderRadius: 10,
            color: allChosen ? '#fff' : C.text3,
            fontSize: 13,
            fontWeight: 700,
            padding: '10px 20px',
            cursor: allChosen ? 'pointer' : 'default',
            transition: 'all 0.2s',
            whiteSpace: 'nowrap',
            boxShadow: allChosen ? '0 4px 14px rgba(249,115,22,0.3)' : 'none',
          }}
        >
          {allChosen ? '→ Generate My Blueprint' : 'Pick one from each column'}
        </button>
      </div>

      {/* Launch price with AI suggestions */}
      {allChosen && (
        <div style={{ marginBottom: 18 }}>
          <label style={{ color: C.text2, fontSize: 13, marginBottom: 10, display: 'block' }}>
            💰 What's your launch price?
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, maxWidth: 220, marginBottom: 10 }}>
            <span style={{ color: C.text2, fontSize: 18 }}>$</span>
            <input
              type="number" min={0}
              value={launchPrice}
              onChange={(e) => { setLaunchPrice(e.target.value); setSelectedPricingTier(''); }}
              placeholder="e.g. 49"
              style={{
                background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
                color: C.text, fontSize: 15, padding: '10px 14px', outline: 'none',
                width: '100%', boxSizing: 'border-box',
              }}
            />
          </div>
          <button
            onClick={fetchPricingSuggestions}
            disabled={pricingLoading}
            style={{
              background: 'rgba(124,111,247,0.12)', border: `1px solid rgba(124,111,247,0.3)`,
              borderRadius: 8, padding: '7px 14px', color: C.primary, fontSize: 12,
              fontWeight: 600, cursor: pricingLoading ? 'not-allowed' : 'pointer',
              opacity: pricingLoading ? 0.6 : 1, transition: 'all 0.15s',
              marginBottom: 12,
            }}
          >
            {pricingLoading ? '⏳ Thinking…' : '✨ Get AI price suggestions'}
          </button>

          {/* Pricing suggestion cards */}
          {pricingSuggestions.length > 0 && (
            <div className="r-grid-3" style={{ gap: 12, marginTop: 8 }}>
              {pricingSuggestions.map((s, i) => {
                const tierEmoji = s.tier === 'entry' ? '🌱' : s.tier === 'recommended' ? '⚡' : '💎';
                const tierLabel = s.tier === 'entry' ? 'ENTRY' : s.tier === 'recommended' ? 'RECOMMENDED' : 'PREMIUM';
                const tierColor = s.tier === 'entry' ? C.text2 : s.tier === 'recommended' ? C.primary : C.amber;
                const isSelected = selectedPricingTier === s.tier;
                return (
                  <div key={i} style={{
                    background: isSelected ? `${tierColor}12` : C.surface,
                    border: `${isSelected ? 2 : 1}px solid ${isSelected ? tierColor : s.tier === 'recommended' ? 'rgba(124,111,247,0.35)' : C.border}`,
                    borderRadius: 14, padding: 16,
                    boxShadow: s.tier === 'recommended' ? `0 0 20px rgba(124,111,247,0.12)` : 'none',
                    animation: `fadeUp 0.4s ease ${i * 0.1}s both`,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }} onClick={() => {
                    setLaunchPrice(String(s.price));
                    setSelectedPricingTier(s.tier);
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: tierColor, letterSpacing: '0.1em', marginBottom: 6 }}>
                      {tierEmoji} {tierLabel}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: C.text, marginBottom: 4 }}>{s.displayPrice}</div>
                    <div style={{ fontSize: 12, color: C.text2, lineHeight: 1.5, marginBottom: 10 }}>{s.bestFor}</div>
                    <div style={{ fontSize: 11, color: C.text3, marginBottom: 4 }}>
                      Conv. rate: {s.expectedConversionRate}
                    </div>
                    <div style={{ fontSize: 11, color: tierColor, marginBottom: 10 }}>
                      50 users → ${s.revenueAt50Users.toLocaleString()}/mo
                    </div>
                    <button style={{
                      width: '100%', background: isSelected ? tierColor : 'transparent',
                      border: `1px solid ${tierColor}`, borderRadius: 8,
                      color: isSelected ? '#fff' : tierColor,
                      fontSize: 11, fontWeight: 700, padding: '6px 0', cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}>
                      {isSelected ? '✓ Selected' : 'Select this price'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {pricingError && (
            <p style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>{pricingError}</p>
          )}
          <p style={{ color: C.text3, fontSize: 11, marginTop: 8 }}>Used to auto-generate your financial baseline</p>
        </div>
      )}

      {/* FIX: #7 — retry button on step error */}
      <ErrMsg msg={error} onRetry={chooseAndGenerate} />
      <GradientBtn onClick={chooseAndGenerate} disabled={!allChosen}>
        {allChosen ? <>→ Generate My Blueprint<CreditCostLabel step={3} isFirst={pipeline.isFirstPipeline} /></> : 'Pick one from each column to continue'}
      </GradientBtn>
      {creditModal && <InsufficientCreditsModal action="PIPELINE_POSITIONING" required={creditModal.required} available={creditModal.available} onClose={() => { setCreditModal(null); fetchBalance(); }} />}
    </div>
  );
}

// ── STEP 4: Validation Path ───────────────────────────────────

function Step4Validation({ pipeline, onDone }: { pipeline: Pipeline; onDone: (updated: Pipeline) => void }) {
  const navigate = useNavigate();
  const { updatePipeline } = usePipelineStore();
  const [loading, setLoading] = useState(false);
  const [resultLoading, setResultLoading] = useState(false);
  const [plan, setPlan] = useState<Record<string, unknown> | null>(
    pipeline.validationPath?.aiOutput ? pipeline.validationPath.aiOutput as Record<string, unknown> : null
  );
  const [showResultForm, setShowResultForm] = useState(!!pipeline.validationPath?.userResults);
  const [results, setResults] = useState({ visits: '', conversions: '', emailsCollected: '', preOrders: '', qualitativeFeedback: '' });
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'dm' | 'email' | 'cold_call'>('dm');
  const [checkedDays, setCheckedDays] = useState<Set<number>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(`pipeline_${pipeline.id}_checklist`) ?? '[]')); }
    catch { return new Set(); }
  });
  const [creditModal, setCreditModal] = useState<{ required: number; available: number } | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  // FIX: #5 — warn before submitting empty validation results
  const [showEmptyWarning, setShowEmptyWarning] = useState(false);
  const { optimisticDeduct, optimisticRestore, fetchBalance } = useCreditsStore();
  const { steps: valSteps, progress: valProgress, finish } = useLoadingSteps([
    'Analyzing your positioning…',
    'Building outreach templates…',
    'Designing 14-day checklist…',
    'Setting success thresholds…',
  ], loading);
  const autoTriggered = useRef(false);

  const toggleDay = (i: number) => {
    setCheckedDays((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      localStorage.setItem(`pipeline_${pipeline.id}_checklist`, JSON.stringify([...next]));
      return next;
    });
  };

  const generate = async () => {
    setLoading(true);
    setError('');
    // FIX: #1 — optimistic credit deduct before AI call, restore on failure
    const COST = CREDIT_COSTS.PIPELINE_VALIDATION;
    let restored = false;
    optimisticDeduct(COST);
    const MAX_RETRIES = 1;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) await new Promise(r => setTimeout(r, 1500 * attempt));
        console.log('[Step4] Generating validation plan...');
        const { data } = await aiApi.post(`/pipeline/${pipeline.id}/validation-path`, {});
        console.log('[Step4] Validation plan response:', data);
        const updatedPipeline = data?.data?.pipeline as Pipeline | undefined;
        const aiOutput = updatedPipeline?.validationPath?.aiOutput;
        if (!aiOutput?.landingHeadline) throw new Error('Validation plan incomplete — missing headline. Try again.');
        // Update store with full pipeline (includes status:'completed' + linkedStartupId)
        if (updatedPipeline?.id) updatePipeline(updatedPipeline);
        finish();
        setTimeout(() => { setPlan(aiOutput as Record<string, unknown>); setLoading(false); }, 400);
        return;
      } catch (err: any) {
        console.error('[Step4] Validation plan error:', err);
        const is5xx = err?.response?.status >= 500;
        const is429 = err?.response?.status === 429;
        const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout');
        if ((is5xx || is429 || isTimeout) && attempt < MAX_RETRIES) { await new Promise(r => setTimeout(r, 2000 * (attempt + 1))); continue; }
        // FIX: #1 — restore credits on failure
        if (!restored) { optimisticRestore(COST); restored = true; }
        if (err?.response?.status === 402) {
          setCreditModal({ required: err.response.data.required, available: err.response.data.available });
        } else {
          setError(is5xx || is429 ? 'High demand right now — please wait 30 seconds and try again. Your credits were not deducted.' : classifyApiError(err));
        }
        fetchBalance();
        setLoading(false);
        return;
      }
    }
  };

  const submitResultsDirect = async () => {
    setResultLoading(true);
    setError('');
    try {
      console.log('[Step4] Submitting validation results...');
      await api.post(`/pipeline/${pipeline.id}/validation-results`, {
        visits: parseInt(results.visits) || 0,
        conversions: parseInt(results.conversions) || 0,
        emailsCollected: parseInt(results.emailsCollected) || 0,
        preOrders: parseInt(results.preOrders) || 0,
        qualitativeFeedback: results.qualitativeFeedback,
      }, { timeout: 60000 });
      console.log('[Step4] Fetching updated pipeline...');
      const { data } = await api.get(`/pipeline/${pipeline.id}`);
      console.log('[Step4] Updated pipeline:', data);
      const updated: Pipeline = data?.data?.pipeline;
      if (!updated?.id) throw new Error('Server returned invalid pipeline after submitting results');
      onDone(updated);
    } catch (err) {
      console.error('[Step4] Submit results error:', err);
      setError(classifyApiError(err));
      setResultLoading(false);
    }
  };

  // FIX: #5 — warn before submitting empty validation results
  const submitResults = () => {
    const totalSignals = (parseInt(results.visits) || 0) + (parseInt(results.conversions) || 0)
      + (parseInt(results.emailsCollected) || 0) + (parseInt(results.preOrders) || 0);
    const hasQualitative = (results.qualitativeFeedback?.trim().length || 0) >= 10;
    if (totalSignals === 0 && !hasQualitative && !showEmptyWarning) {
      setShowEmptyWarning(true);
      return;
    }
    setShowEmptyWarning(false);
    submitResultsDirect();
  };

  // Auto-trigger validation plan generation when entering this step
  useEffect(() => {
    if (!plan && !loading && !autoTriggered.current) {
      autoTriggered.current = true;
      generate();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <LoadingCinematic stage="Building your validation plan..." steps={valSteps} progress={valProgress} subtitle="Creating your 14-day action plan" creditReassurance />;

  if (!plan) {
    return (
      <div style={{ maxWidth: 500, margin: '0 auto', textAlign: 'center', animation: 'fadeUp 0.4s ease' }}>
        <div style={{ fontSize: 52, marginBottom: 20, animation: 'float 3s ease-in-out infinite' }}>📋</div>
        <h2 style={{ fontSize: 26, fontWeight: 800, color: C.text, marginBottom: 12 }}>Your Validation Plan</h2>
        <p style={{ color: C.text2, fontSize: 14, lineHeight: 1.7, marginBottom: 32 }}>
          14-day step-by-step plan to test your idea with real potential users.
        </p>
        {/* FIX: #7 — retry button on step error */}
        <ErrMsg msg={error} onRetry={generate} />
        <GradientBtn onClick={generate}>→ Generate My Validation Plan<CreditCostLabel step={4} isFirst={pipeline.isFirstPipeline} /></GradientBtn>
        {creditModal && <InsufficientCreditsModal action="PIPELINE_VALIDATION" required={creditModal.required} available={creditModal.available} onClose={() => { setCreditModal(null); fetchBalance(); }} />}
      </div>
    );
  }

  const p = plan;

  // Safe-parse fields that may arrive as JSON strings from the API
  const parseField = <T,>(val: unknown, fallback: T): T => {
    if (val == null) return fallback;
    if (typeof val === 'string') { try { return JSON.parse(val); } catch { return fallback; } }
    return val as T;
  };
  const checklist = parseField<Record<string, string>[]>(p.checklist, []);
  const testingChannels = parseField<Record<string, string>[]>(p.testingChannels, []);
  const successThreshold = parseField<Record<string, string>>(p.successThreshold, {});

  return (
    <div style={{ maxWidth: 840, margin: '0 auto', animation: 'fadeUp 0.4s ease' }}>
      {/* Landing copy */}
      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 20 }}>🖥 Landing Page Copy</h3>
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <p style={{ color: C.text2, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>Headline</p>
            <CopyBtn text={S(p.landingHeadline) ?? ''} />
          </div>
          <p style={{ color: C.text, fontSize: 20, fontWeight: 700 }}>{S(p.landingHeadline)}</p>
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <p style={{ color: C.text2, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>Subheadline</p>
            <CopyBtn text={S(p.landingSubheadline) ?? ''} />
          </div>
          <p style={{ color: C.text2, fontSize: 15 }}>{S(p.landingSubheadline)}</p>
        </div>
        <div style={{ background: `${C.primary}0d`, border: `1px solid ${C.primary}30`, borderRadius: 10, padding: 14 }}>
          <p style={{ color: C.primary, fontSize: 11, fontWeight: 700, marginBottom: 5 }}>Test Offer</p>
          <p style={{ color: C.text2, fontSize: 13 }}>{S(p.testOffer)}</p>
        </div>
      </Card>

      {/* Outreach */}
      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 16 }}>📨 Outreach Messages</h3>
        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          {(['dm', 'email', 'cold_call'] as const).map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              style={{
                padding: '5px 16px', borderRadius: 8,
                border: `1px solid ${activeTab === tab ? '#f97316' : C.border}`,
                background: activeTab === tab ? '#f97316' : 'transparent',
                color: activeTab === tab ? '#fff' : C.text2,
                cursor: 'pointer', fontSize: 12, fontWeight: activeTab === tab ? 600 : 500,
                transition: 'all 0.2s',
              }}>
              {tab === 'dm' ? '💬 DM' : tab === 'email' ? '📧 Email' : '📞 Cold Call'}
            </button>
          ))}
        </div>
        {activeTab === 'dm' && !!p.dmMessage && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}><CopyBtn text={S(p.dmMessage)} /></div>
            <div style={{ background: C.bg, borderRadius: 10, padding: 18, color: C.text2, fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{S(p.dmMessage)}</div>
            <p style={{ fontSize: 11, color: C.text3, marginTop: 8, fontStyle: 'italic' }}>
              Use this in: LinkedIn DMs, Twitter/X DMs, Reddit r/{S(p.targetNiche)?.toLowerCase().replace(/\s+/g, '') || 'yourniche'}
            </p>
          </div>
        )}
        {activeTab === 'email' && (
          <div>
            {!!p.emailSubject && <div style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <p style={{ color: C.text2, fontSize: 11 }}>Subject line</p><CopyBtn text={S(p.emailSubject)} />
              </div>
              <p style={{ color: C.text, fontWeight: 600, fontSize: 13 }}>{S(p.emailSubject)}</p>
            </div>}
            {!!p.emailBody && <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}><CopyBtn text={S(p.emailBody)} /></div>
              <div style={{ background: C.bg, borderRadius: 10, padding: 18, color: C.text2, fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{S(p.emailBody)}</div>
              <p style={{ fontSize: 11, color: C.text3, marginTop: 8, fontStyle: 'italic' }}>
                Use this for: Cold email campaigns, newsletter outreach, LinkedIn InMail
              </p>
            </div>}
          </div>
        )}
        {activeTab === 'cold_call' && (() => {
          const script = S(p.coldCallScript) || `Hey [Name], I'm building ${S(p.productName) || '[Product]'} — ${S(p.elevatorPitch) || 'a tool that solves [problem]'}.\n\nI know you ${S(p.problemSummary) || '[pain point]'}. I'm looking for 5 early testers to try it free in exchange for 10 minutes of feedback.\n\nWould you be open to a quick call this week?`;
          return (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}><CopyBtn text={script} /></div>
              <div style={{ background: C.bg, borderRadius: 10, padding: 18, color: C.text2, fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {script}
              </div>
              <p style={{ fontSize: 11, color: C.text3, marginTop: 8, fontStyle: 'italic' }}>
                Use this for: Phone calls, video call intros, in-person conversations, voice messages
              </p>
            </div>
          );
        })()}
      </Card>

      {/* 14-day checklist */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>📅 14-Day Checklist</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {(() => {
              const sid = pipeline.linkedStartupId;
              const dl = sid ? localStorage.getItem(`validationDeadline_${sid}`) : null;
              if (!dl) return null;
              const daysLeft = Math.max(0, Math.ceil((Number(dl) - Date.now()) / 86400000));
              return (
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 8,
                  background: daysLeft <= 3 ? 'rgba(255,68,102,0.15)' : 'rgba(124,111,247,0.1)',
                  color: daysLeft <= 3 ? C.danger : C.primary,
                  border: `1px solid ${daysLeft <= 3 ? 'rgba(255,68,102,0.3)' : 'rgba(124,111,247,0.2)'}`,
                }}>
                  {daysLeft === 0 ? 'Deadline passed' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
                </span>
              );
            })()}
            <span style={{ color: C.text2, fontSize: 12 }}>{checkedDays.size}/{checklist.length} done</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {checklist.map((item, i) => (
            <div key={i} onClick={() => toggleDay(i)}
              style={{
                display: 'flex', gap: 12, padding: '12px 14px',
                background: checkedDays.has(i) ? `${C.green}0d` : C.bg,
                borderRadius: 10, cursor: 'pointer',
                border: `1px solid ${checkedDays.has(i) ? `${C.green}30` : C.border}`,
                transition: 'all 0.15s',
                opacity: checkedDays.has(i) ? 0.6 : 1,
              }}>
              <div style={{
                width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                border: `2px solid ${checkedDays.has(i) ? C.green : C.text3}`,
                background: checkedDays.has(i) ? C.green : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {checkedDays.has(i) && <span style={{ color: '#000', fontSize: 11, fontWeight: 700 }}>✓</span>}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ color: C.primary, fontSize: 11, fontWeight: 700 }}>{item.day}</span>
                  <span style={{ color: C.text3, fontSize: 10 }}>{item.metric}</span>
                </div>
                <p style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 2, textDecoration: checkedDays.has(i) ? 'line-through' : 'none' }}>{item.task}</p>
                <p style={{ color: C.text2, fontSize: 12 }}>{item.goal}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Testing channels */}
      {testingChannels.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 16 }}>📡 Testing Channels</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
            {testingChannels.map((ch, i) => (
              <div key={i} style={{ background: C.bg, borderRadius: 12, padding: 16, border: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>{ch.channel}</span>
                  {ch.expectedConversionRate && <span style={{ color: C.green, fontSize: 11, fontWeight: 600, background: `${C.green}15`, padding: '2px 8px', borderRadius: 6 }}>{ch.expectedConversionRate} conv.</span>}
                </div>
                {ch.why && <p style={{ color: C.text2, fontSize: 12, marginBottom: 8, lineHeight: 1.5 }}>{ch.why}</p>}
                {ch.howTo && <p style={{ color: C.text2, fontSize: 12, marginBottom: 6, lineHeight: 1.5 }}><span style={{ color: C.primary, fontWeight: 600 }}>How: </span>{ch.howTo}</p>}
                {ch.timeInvestment && <p style={{ color: C.text3, fontSize: 11 }}>⏱ {ch.timeInvestment}</p>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Success threshold */}
      {(successThreshold.minimum || successThreshold.good || successThreshold.exceptional) && (
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 16 }}>🎯 Success Threshold</h3>
          <div className="r-grid-3" style={{ gap: 12 }}>
            {([['Minimum', successThreshold.minimum, C.amber], ['Good', successThreshold.good, C.primary], ['Exceptional 🚀', successThreshold.exceptional, C.green]] as [string, string, string][]).map(([label, val, color]) => (
              <div key={label} style={{ background: C.bg, borderRadius: 10, padding: 14, border: `1px solid ${color}30` }}>
                <p style={{ color, fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{label}</p>
                <p style={{ color: C.text2, fontSize: 12 }}>{val}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Results */}
      {!showResultForm ? (
        <div style={{
          background: 'rgba(0,230,118,0.08)', border: '1px solid rgba(0,230,118,0.2)',
          borderRadius: 16, padding: 32, textAlign: 'center', marginTop: 32,
        }}>
          {/* FIX: #8 — store 14-day validation deadline in localStorage */}
          {(() => {
            const sid = pipeline.linkedStartupId;
            if (sid && !localStorage.getItem(`validationDeadline_${sid}`)) {
              localStorage.setItem(`validationDeadline_${sid}`, String(Date.now() + 14 * 24 * 60 * 60 * 1000));
            }
            return null;
          })()}
          <div style={{ fontSize: 48, marginBottom: 16 }}>🚀</div>
          <h3 style={{ fontSize: 24, fontWeight: 700, color: C.text, marginBottom: 12 }}>
            Your 14-day experiment starts now
          </h3>
          <p style={{ color: C.text2, fontSize: 15, marginBottom: 24, lineHeight: 1.6 }}>
            Execute your validation plan. Come back to your dashboard in 7–14 days to log results
            and unlock your personalized MVP Blueprint.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                const { pipelineReviewGiven } = useCreditsStore.getState();
                if (!pipelineReviewGiven) {
                  setShowReviewModal(true);
                } else {
                  navigate('/startups');
                }
              }}
              style={{
                background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`,
                border: 'none', borderRadius: 12, color: '#fff',
                fontSize: 15, fontWeight: 700, padding: '13px 24px', cursor: 'pointer',
              }}
            >
              Save plan &amp; exit →
            </button>
            <button
              onClick={() => setShowResultForm(true)}
              style={{
                background: 'transparent', border: `1px solid ${C.border}`,
                borderRadius: 12, color: C.text2, fontSize: 14,
                padding: '13px 24px', cursor: 'pointer',
              }}
            >
              I already have results →
            </button>
          </div>
        </div>
      ) : (
        <Card style={{ border: `1px solid ${C.primary}40` }}>
          <h3 style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 18 }}>📊 Enter Your Validation Results</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14, marginBottom: 14 }}>
            {([['visits', '🌐 Total landing page visits'], ['conversions', '✅ Conversions'], ['emailsCollected', '📧 Emails collected'], ['preOrders', '💳 Pre-orders / paid interest']] as [string, string][]).map(([field, label]) => (
              <div key={field}>
                <label style={{ color: C.text2, fontSize: 12, display: 'block', marginBottom: 5 }}>{label}</label>
                <input type="number" min={0} value={(results as Record<string, string>)[field]}
                  onChange={(e) => setResults({ ...results, [field]: e.target.value })}
                  style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 14, padding: '9px 12px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            ))}
          </div>
          <div style={{ marginBottom: 18 }}>
            <label style={{ color: C.text2, fontSize: 12, display: 'block', marginBottom: 5 }}>💬 Qualitative feedback (what people said)</label>
            <textarea value={results.qualitativeFeedback} onChange={(e) => setResults({ ...results, qualitativeFeedback: e.target.value })}
              rows={4} style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, padding: '10px 14px', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
          </div>
          {/* FIX: #5 — empty results warning banner */}
          {showEmptyWarning && (
            <div style={{
              background: `${C.amber}15`, border: `1px solid ${C.amber}40`,
              borderRadius: 10, padding: '12px 16px', marginBottom: 16,
            }}>
              <p style={{ color: C.amber, fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                All fields are empty or zero
              </p>
              <p style={{ color: C.text2, fontSize: 12, lineHeight: 1.5 }}>
                Your analysis will be more useful with real validation data. Are you sure you want to continue?
              </p>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button onClick={() => setShowEmptyWarning(false)} style={{
                  background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8,
                  color: C.text2, fontSize: 12, padding: '6px 14px', cursor: 'pointer',
                }}>Go back and add data</button>
                <button onClick={() => { setShowEmptyWarning(false); submitResultsDirect(); }} style={{
                  background: `${C.amber}20`, border: `1px solid ${C.amber}40`, borderRadius: 8,
                  color: C.amber, fontSize: 12, fontWeight: 600, padding: '6px 14px', cursor: 'pointer',
                }}>Submit anyway</button>
              </div>
            </div>
          )}
          {/* FIX: #7 — retry button on step error */}
          <ErrMsg msg={error} onRetry={submitResults} />
          <GradientBtn onClick={submitResults} disabled={resultLoading}>
            {resultLoading ? 'Analyzing results…' : '→ Analyze My Results'}
          </GradientBtn>
        </Card>
      )}
      {showReviewModal && (
        <ReviewModal
          pipelineId={pipeline.id}
          onClose={() => {
            setShowReviewModal(false);
            navigate('/startups');
          }}
        />
      )}
    </div>
  );
}

// ── STEP 6: MVP Blueprint ─────────────────────────────────────

function Step6MVP({ pipeline, onDone }: { pipeline: Pipeline; onDone: (updated: Pipeline) => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confetti, setConfetti] = useState(false);
  const [creditModal, setCreditModal] = useState<{ required: number; available: number } | null>(null);
  const { optimisticDeduct, optimisticRestore, fetchBalance } = useCreditsStore();
  const bp = (pipeline.mvpBlueprint?.aiOutput ?? null) as Record<string, unknown> | null;
  const { finish } = useLoadingSteps([
    'Reviewing your entire journey…',
    'Selecting optimal tech stack…',
    'Planning build phases…',
    'Estimating budget…',
    'Generating checklist…',
  ], loading);

  const generate = async () => {
    setLoading(true);
    setError('');
    // FIX: #1 — optimistic credit deduct before AI call, restore on failure
    const COST = CREDIT_COSTS.PIPELINE_MVP;
    let restored = false;
    optimisticDeduct(COST);
    const MAX_RETRIES = 1;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) await new Promise(r => setTimeout(r, 1500 * attempt));
        console.log('[Step6] Generating MVP blueprint...');
        const { data } = await aiApi.post(`/pipeline/${pipeline.id}/mvp-blueprint`, {});
        console.log('[Step6] MVP blueprint response:', data);
        const updated: Pipeline = data?.data?.pipeline;
        if (!updated?.id) throw new Error('Server returned invalid pipeline after MVP generation');
        if (!updated.mvpBlueprint?.aiOutput?.coreFeature) throw new Error('MVP blueprint incomplete — missing core feature. Try again.');
        finish();
        setTimeout(() => {
          onDone(updated);
          setLoading(false);
          setConfetti(true);
          setTimeout(() => setConfetti(false), 3500);
          if (updated.linkedStartupId) {
            const price = extractPriceFromBlueprint(updated.mvpBlueprint?.aiOutput ?? {});
            const budgetMin = (updated.mvpBlueprint?.aiOutput?.budgetEstimate as Record<string, number>)?.min ?? 500;
            const monthlyFixed = Math.max(100, Math.round(budgetMin / 6));
            api.post('/scenarios', {
              startupId: updated.linkedStartupId,
              name: 'Base Scenario',
              assumptions: {
                monthlyFixedCosts: monthlyFixed,
                variableCostPerUnit: 0,
                initialUsers: 10,
                monthlyGrowthRate: 0.1,
                pricePerUnit: price,
                churnRate: 0.05,
                projectionMonths: 12,
              },
            }).catch(() => {});
          }
        }, 400);
        return;
      } catch (err: any) {
        console.error('[Step6] MVP generate error:', err);
        const is5xx = err?.response?.status >= 500;
        const is429 = err?.response?.status === 429;
        const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout');
        if ((is5xx || is429 || isTimeout) && attempt < MAX_RETRIES) { await new Promise(r => setTimeout(r, 2000 * (attempt + 1))); continue; }
        // FIX: #1 — restore credits on failure
        if (!restored) { optimisticRestore(COST); restored = true; }
        if (err?.response?.status === 402) {
          setCreditModal({ required: err.response.data.required, available: err.response.data.available });
        } else {
          setError(isTimeout ? 'MVP Blueprint generation timed out. This is the most complex analysis — please try again.' :
            is5xx || is429 ? 'High demand right now — please wait 30 seconds and try again. Your credits were not deducted.' : classifyApiError(err));
        }
        fetchBalance();
        setLoading(false);
        return;
      }
    }
  };

  if (loading) {
    return <AIThinkingScreen step={6} stage="Generating your MVP blueprint..." />;
  }

  if (!bp) {
    return (
      <div style={{ maxWidth: 500, margin: '0 auto', textAlign: 'center', animation: 'fadeUp 0.4s ease' }}>
        <div style={{ fontSize: 52, marginBottom: 20, animation: 'float 3s ease-in-out infinite' }}>🏗</div>
        <h2 style={{ fontSize: 26, fontWeight: 800, color: C.text, marginBottom: 12 }}>MVP Blueprint</h2>
        <p style={{ color: C.text2, fontSize: 14, lineHeight: 1.7, marginBottom: 32 }}>
          Claude will generate a personalized, step-by-step build plan tailored to your skills, budget, and validated idea.
        </p>
        {/* FIX: #7 — retry button on step error */}
        <ErrMsg msg={error} onRetry={generate} />
        <GradientBtn onClick={generate}>→ Generate My MVP Blueprint</GradientBtn>
        {creditModal && <InsufficientCreditsModal action="PIPELINE_MVP" required={creditModal.required} available={creditModal.available} onClose={() => { setCreditModal(null); fetchBalance(); }} />}
      </div>
    );
  }

  const stack = (bp.recommendedStack as Record<string, unknown>) ?? {};
  const timeline = (bp.timeline as Record<string, unknown>) ?? {};
  const budget = (bp.budgetEstimate as Record<string, unknown>) ?? {};
  const checklist = (bp.buildChecklist as Record<string, string>[]) ?? [];
  const doNotBuild = (bp.doNotBuild as Record<string, string>[]) ?? [];
  const metrics = (bp.successMetrics as Record<string, string>[]) ?? [];

  const p0 = checklist.filter((t) => t.priority === 'P0');
  const p1 = checklist.filter((t) => t.priority === 'P1');
  const p2 = checklist.filter((t) => t.priority === 'P2');

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', animation: 'fadeUp 0.4s ease' }} id="mvp-blueprint">
      <Confetti active={confetti} />

      {/* Hero */}
      <div style={{
        background: `linear-gradient(135deg, ${C.primary}18, ${C.accent}0d)`,
        border: `1px solid ${C.primary}40`,
        borderRadius: 20, padding: '32px 36px', marginBottom: 24, textAlign: 'center',
      }}>
        <div style={{ color: C.primary, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 8 }}>Your MVP Blueprint</div>
        <h2 style={{ fontSize: 26, fontWeight: 900, color: C.text, marginBottom: 16 }}>
          {S(pipeline.idea?.aiOutput?.ideaSnapshot) ?? pipeline.idea?.rawIdea}
        </h2>
        <button onClick={() => window.print()}
          style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, color: C.text2, fontSize: 12, padding: '7px 16px', cursor: 'pointer' }}>
          ⬇ Download PDF
        </button>
      </div>

      {/* Core feature */}
      <Card style={{ border: `2px solid ${C.primary}`, marginBottom: 16 }}>
        <h3 style={{ color: C.primary, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 10 }}>🎯 Build This One Thing</h3>
        <h2 style={{ color: C.text, fontSize: 20, fontWeight: 800, marginBottom: 6 }}>{S(bp.coreFeature)}</h2>
        <p style={{ color: C.text2, fontSize: 13, lineHeight: 1.5, marginBottom: 14 }}>{S(bp.coreFeatureReasoning)}</p>
        {doNotBuild.length > 0 && (
          <div style={{ background: `${C.danger}0d`, border: `1px solid ${C.danger}30`, borderRadius: 10, padding: 14 }}>
            <p style={{ color: C.danger, fontSize: 11, fontWeight: 700, marginBottom: 8 }}>🚫 DO NOT BUILD (yet)</p>
            {doNotBuild.map((item, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <span style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{item.feature}: </span>
                <span style={{ color: C.text2, fontSize: 12 }}>{item.reason}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Stack */}
      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 18 }}>💻 Recommended Stack</h3>
        <div className="r-grid-4" style={{ marginBottom: 14 }}>
          {([['Frontend', S(stack.frontend), '#6366f1'], ['Backend', S(stack.backend), C.green], ['Database', S(stack.database), C.amber], ['Hosting', S(stack.hosting), C.accent]] as [string, string, string][]).map(([label, val, color]) => (
            <div key={label} style={{ background: C.bg, borderRadius: 10, padding: 14, border: `1px solid ${color}30` }}>
              <p style={{ color, fontSize: 10, fontWeight: 700, marginBottom: 5, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</p>
              <p style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{val}</p>
            </div>
          ))}
        </div>
        {!!stack.reasoning && <p style={{ color: C.text2, fontSize: 12, lineHeight: 1.5 }}>{S(stack.reasoning)}</p>}
      </Card>

      {/* Timeline */}
      {Array.isArray(timeline.phases) && (timeline.phases as Record<string, string>[]).length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 18 }}>📅 Timeline ({timeline.weeks as number} weeks)</h3>
          <div style={{ display: 'flex', gap: 0, alignItems: 'stretch', overflowX: 'auto' }}>
            {(timeline.phases as Record<string, string>[]).map((phase, i) => (
              <div key={i} style={{ flex: 1, minWidth: 150, padding: '0 12px', borderLeft: i > 0 ? `1px solid ${C.border}` : 'none' }}>
                <div style={{ color: C.primary, fontSize: 11, fontWeight: 700, marginBottom: 5 }}>Phase {i + 1}</div>
                <div style={{ color: C.text, fontSize: 13, fontWeight: 700, marginBottom: 3 }}>{phase.phase}</div>
                <div style={{ color: C.text2, fontSize: 11, marginBottom: 6 }}>{phase.duration}</div>
                <div style={{ color: C.text2, fontSize: 12, lineHeight: 1.4 }}>{phase.deliverable}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Budget */}
      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 14 }}>💰 Budget Estimate</h3>
        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          <div style={{ background: C.bg, borderRadius: 10, padding: 14, flex: 1, textAlign: 'center' }}>
            <p style={{ color: C.text2, fontSize: 11, marginBottom: 3 }}>Minimum</p>
            <p style={{ color: C.green, fontSize: 26, fontWeight: 800 }}>${budget.min as number}</p>
          </div>
          <div style={{ background: C.bg, borderRadius: 10, padding: 14, flex: 1, textAlign: 'center' }}>
            <p style={{ color: C.text2, fontSize: 11, marginBottom: 3 }}>Maximum</p>
            <p style={{ color: C.amber, fontSize: 26, fontWeight: 800 }}>${budget.max as number}</p>
          </div>
        </div>
        {Array.isArray(budget.breakdown) && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
              <th style={{ color: C.text2, fontSize: 11, textAlign: 'left', padding: '6px 0' }}>Item</th>
              <th style={{ color: C.text2, fontSize: 11, textAlign: 'right', padding: '6px 0' }}>Cost</th>
            </tr></thead>
            <tbody>
              {(budget.breakdown as Record<string, string>[]).map((row, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.surface2}` }}>
                  <td style={{ color: C.text2, fontSize: 12, padding: '7px 0' }}>{row.item}</td>
                  <td style={{ color: C.text, fontSize: 12, textAlign: 'right', padding: '7px 0' }}>{row.cost}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Build checklist */}
      {checklist.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 18 }}>✅ Build Checklist</h3>
          {([['P0', 'Must Build', p0, C.danger], ['P1', 'Should Build', p1, C.amber], ['P2', 'Nice to Have', p2, C.text2]] as [string, string, Record<string, string>[], string][]).map(([priority, label, items, color]) =>
            items.length > 0 ? (
              <div key={priority} style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ background: `${color}20`, color, borderRadius: 5, padding: '2px 9px', fontSize: 11, fontWeight: 700 }}>{priority}</span>
                  <span style={{ color: C.text2, fontSize: 12 }}>{label}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {items.map((task, i) => (
                    <div key={i} style={{ display: 'flex', gap: 12, background: C.bg, borderRadius: 9, padding: '10px 14px', border: `1px solid ${C.border}` }}>
                      <span style={{ color, fontSize: 14, flexShrink: 0 }}>○</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                          <span style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{task.task}</span>
                          <span style={{ color: C.text3, fontSize: 11 }}>{task.timeEstimate}</span>
                        </div>
                        <p style={{ color: C.text2, fontSize: 11 }}>{task.why}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null
          )}
        </Card>
      )}

      {/* Success metrics */}
      {metrics.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 14 }}>📊 Success Metrics</h3>
          <div className="r-grid-auto" style={{ gap: 12 }}>
            {metrics.map((m, i) => (
              <div key={i} style={{ background: C.bg, borderRadius: 10, padding: 14, border: `1px solid ${C.border}` }}>
                <p style={{ color: C.primary, fontSize: 12, fontWeight: 700, marginBottom: 5 }}>{m.metric}</p>
                <p style={{ color: C.text, fontSize: 20, fontWeight: 800, marginBottom: 3 }}>{m.target}</p>
                <p style={{ color: C.text2, fontSize: 11 }}>{m.timeframe}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!!bp.founderSpecificAdvice && (
        <div style={{ background: `${C.primary}0d`, border: `1px solid ${C.primary}30`, borderRadius: 12, padding: 22, marginBottom: 24 }}>
          <p style={{ color: C.primary, fontSize: 11, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Advice For You Specifically</p>
          <p style={{ color: C.text2, fontSize: 14, lineHeight: 1.6 }}>{S(bp.founderSpecificAdvice)}</p>
        </div>
      )}

      <button
        onClick={() => onDone({ ...pipeline, status: 'completed' })}
        style={{
          width: '100%', background: `linear-gradient(135deg, ${C.green}, #16a34a)`,
          border: 'none', borderRadius: 14, color: '#fff', fontSize: 17, fontWeight: 700,
          padding: '16px 0', cursor: 'pointer', height: 56,
        }}
      >
        🎉 Mark as Complete
      </button>
    </div>
  );
}

// ── Pipeline Complete Sequence ─────────────────────────────────

function PipelineCompleteSequence({ pipeline, onComplete }: { pipeline: Pipeline; onComplete: () => void }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<'loading' | 'celebration'>('loading');
  const [checkItems, setCheckItems] = useState<number>(0);
  const { steps: completeSteps, progress: completeProgress, finish: completeFinish } = useLoadingSteps([
    'Designing your validation experiment…',
    'Writing outreach scripts…',
    'Generating landing page copy…',
    'Setting success metrics…',
    'Packaging your action plan…',
  ], true);

  // Phase 1: Loading screen (minimum 5 seconds)
  useEffect(() => {
    const timer = setTimeout(() => {
      completeFinish();
      setTimeout(() => setPhase('celebration'), 500);
    }, 5000);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 2: Celebration auto-advance
  useEffect(() => {
    if (phase !== 'celebration') return;
    // Animate checklist items one by one
    const items = [0, 1, 2, 3, 4];
    items.forEach((i) => {
      setTimeout(() => setCheckItems(i + 1), (i + 1) * 500);
    });
    // Auto-advance after 6 seconds
    const timer = setTimeout(() => {
      if (pipeline.linkedStartupId) {
        navigate(`/startups/${pipeline.linkedStartupId}/dashboard`);
      } else {
        onComplete();
      }
    }, 6000);
    return () => clearTimeout(timer);
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  if (phase === 'loading') {
    return (
      <div style={{
        position: 'fixed', inset: 0, background: C.bg, zIndex: 200,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <LoadingCinematic
          stage="Building your 14-day plan..."
          steps={completeSteps}
          progress={completeProgress}
          creditReassurance
        />
      </div>
    );
  }

  // Phase 2: Celebration
  const celebrationItems = [
    'Founder archetype & risk profile',
    'Structured idea with clarity score',
    'Reality check with market data',
    'Positioning strategy & elevator pitch',
    '14-day validation experiment',
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, background: C.bg, zIndex: 200,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '40px 24px', animation: 'fadeUp 0.4s ease',
    }}>
      <Confetti active />

      <div style={{ fontSize: 64, marginBottom: 20, animation: 'float 3s ease-in-out infinite' }}>✅</div>

      <h1 style={{
        fontSize: 32, fontWeight: 900, color: C.text, marginBottom: 8, textAlign: 'center',
      }}>
        🎉 Your Pipeline Is Complete
      </h1>

      <p style={{ color: C.text2, fontSize: 15, marginBottom: 28, textAlign: 'center' }}>
        In under 30 minutes, you built this:
      </p>

      <div style={{ maxWidth: 400, width: '100%', marginBottom: 32 }}>
        {celebrationItems.map((item, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 0',
            opacity: i < checkItems ? 1 : 0,
            transform: i < checkItems ? 'translateX(0)' : 'translateX(-10px)',
            transition: 'all 0.4s ease',
          }}>
            <span style={{ color: C.green, fontSize: 16, flexShrink: 0 }}>✓</span>
            <span style={{ color: C.text, fontSize: 14 }}>{item}</span>
          </div>
        ))}
      </div>

      <p style={{ color: C.text3, fontSize: 13, marginBottom: 24 }}>
        A consultant would charge $2,000–$5,000 for this.
      </p>

      <button
        onClick={() => {
          if (pipeline.linkedStartupId) {
            navigate(`/startups/${pipeline.linkedStartupId}/dashboard`);
          } else {
            onComplete();
          }
        }}
        style={{
          background: C.orange,
          border: 'none', borderRadius: 14, color: '#fff',
          fontSize: 17, fontWeight: 700, padding: '16px 40px', cursor: 'pointer',
          boxShadow: `0 4px 24px ${C.orangeGlow}`,
        }}
      >
        See My Dashboard →
      </button>
    </div>
  );
}

// ── Main Pipeline Page ────────────────────────────────────────

const STEP_TITLES = [
  'Who are you?',
  "What's your idea?",
  'Brutal Reality Check',
  'Strategic Narrowing',
  'Validation Plan',
];

export default function PipelinePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { pipeline, loading, loadPipeline, updatePipeline } = usePipelineStore();
  const [displayStep, setDisplayStep] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [showLaunchScreen, setShowLaunchScreen] = useState(false);
  const [showCompleteSequence, setShowCompleteSequence] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (id) loadPipeline(id);
  }, [id, loadPipeline]);

  // Update browser tab title with pipeline name
  useEffect(() => {
    if (!pipeline) return;
    const name = pipeline.title || S(pipeline.idea?.aiOutput?.ideaSnapshot) || '';
    document.title = name ? `${name} — StartupScore` : 'Pipeline — StartupScore';
    return () => { document.title = 'StartupScore'; };
  }, [pipeline?.title, pipeline?.idea?.aiOutput]);

  // Fix: [Bug 1] — Guard against displayStep > 4 (max valid step index)
  useEffect(() => {
    if (!pipeline) return;
    if (pipeline.status === 'completed' || pipeline.currentStep >= 5) {
      // Pipeline is done — show the verdict/completion screen
      setShowLaunchScreen(true);
    }
    setDisplayStep(Math.min(pipeline.currentStep, 4));
  }, [pipeline?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDone = useCallback(async (updated: Pipeline) => {
    console.log('[Pipeline] handleDone called, pipeline.id:', updated?.id, 'nextStep:', updated?.currentStep);
    if (!updated?.id) {
      console.error('[Pipeline] handleDone received invalid pipeline:', updated);
      return;
    }
    try {
      updatePipeline(updated);
      // Show completion sequence after pipeline completes (validation plan generated)
      if (updated.status === 'completed') {
        setShowCompleteSequence(true);
        return;
      }
      setTransitioning(true);
      await new Promise<void>((resolve) => setTimeout(resolve, 240));
      setDisplayStep(Math.min(updated.currentStep, 4));
      await new Promise<void>((resolve) => setTimeout(resolve, 50));
      setTransitioning(false);
      contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      console.log('[Pipeline] Now on step:', updated.currentStep);
    } catch (err) {
      console.error('[Pipeline] handleDone transition error:', err);
      setDisplayStep(Math.min(updated.currentStep, 4));
      setTransitioning(false);
    }
  }, [updatePipeline, navigate]);

  const handleStepClick = useCallback((step: number) => {
    // Fix: [Bug 1] — Clamp step to valid range 0-4
    if (!pipeline || step > pipeline.currentStep || step > 4) return;
    setTransitioning(true);
    setTimeout(() => {
      setDisplayStep(Math.min(step, 4));
      setTransitioning(false);
      contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 220);
  }, [pipeline]);

  if (showCompleteSequence && pipeline) {
    return (
      <PipelineCompleteSequence
        pipeline={pipeline}
        onComplete={() => {
          setShowCompleteSequence(false);
          setShowLaunchScreen(true);
        }}
      />
    );
  }

  if (showLaunchScreen && pipeline) {
    return (
      <>
        <LaunchMomentumScreen
          pipeline={pipeline}
          onDismiss={() => { setShowLaunchScreen(false); navigate('/pipeline'); }}
        />
        {showReviewModal && (
          <ReviewModal
            pipelineId={pipeline.id}
            onClose={() => {
              setShowReviewModal(false);
              if (pipeline.linkedStartupId) {
                navigate(`/startups/${pipeline.linkedStartupId}/dashboard`);
              }
            }}
          />
        )}
      </>
    );
  }

  if (loading || !pipeline) {
    return (
      <div style={{
        minHeight: '100vh', background: C.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16,
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: `conic-gradient(from 0deg, ${C.primary}, ${C.accent}, rgba(124,111,247,0.1), ${C.primary})`,
          animation: 'spin 1.2s linear infinite',
          position: 'relative',
        }}>
          <div style={{ width: 40, height: 40, background: C.bg, borderRadius: '50%', position: 'absolute', top: 8, left: 8 }} />
        </div>
        <p style={{ color: C.text2, fontSize: 14 }}>Loading pipeline…</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', flexDirection: 'column', position: 'relative' }}>

      {/* ── Ambient background (matches app shell) ── */}
      <div aria-hidden style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.45) 1px, transparent 1px)',
          backgroundSize: '32px 32px', opacity: 0.07,
        }} />
        <div style={{
          position: 'absolute', width: 900, height: 900, borderRadius: '50%',
          top: -400, left: -300,
          background: 'radial-gradient(circle, rgba(124,111,247,0.1) 0%, transparent 68%)',
          animation: 'orbFloat1 30s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute', width: 700, height: 700, borderRadius: '50%',
          bottom: -200, right: -200,
          background: 'radial-gradient(circle, rgba(249,115,22,0.08) 0%, transparent 70%)',
          animation: 'orbFloat2 38s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute', width: 500, height: 500, borderRadius: '50%',
          top: '40%', left: '10%',
          background: 'radial-gradient(circle, rgba(0,212,255,0.05) 0%, transparent 70%)',
          animation: 'orbFloat3 22s ease-in-out infinite',
        }} />
      </div>

      {/* ── Content at z:1 ── */}
      {/* Top bar */}
      <div style={{
        background: 'rgba(5,5,16,0.82)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${C.border}`,
        padding: '0 24px', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0, position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <LogoLockup size="md" />
        </div>

        <StepIndicator current={displayStep} max={pipeline.currentStep} onStepClick={handleStepClick} />

        <button
          onClick={() => navigate('/pipeline')}
          style={{
            background: 'transparent', border: `1px solid ${C.border}`,
            borderRadius: 8, color: C.text2, fontSize: 12, padding: '7px 14px', cursor: 'pointer',
          }}
        >
          ✕ Save & Exit
        </button>
      </div>

      {/* Profile context banner */}
      {!!pipeline?.userProfile?.aiOutput?.founderType && displayStep > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '7px 16px', background: 'rgba(124,111,247,0.08)',
          border: '1px solid rgba(124,111,247,0.15)',
          borderRadius: 24, margin: '16px auto 0', width: 'fit-content', fontSize: 13,
        }}>
          <span style={{ color: '#7c6ff7' }}>⚡</span>
          <span style={{ color: '#8888aa' }}>You are:</span>
          <span style={{ color: '#f0f0ff', fontWeight: 600 }}>
            {String(pipeline.userProfile.aiOutput.founderType)}
          </span>
          <span style={{ color: '#444466' }}>·</span>
          <span style={{ color: '#8888aa', fontSize: 12 }}>Advice personalized to your profile</span>
        </div>
      )}

      {/* Step title */}
      <div style={{ textAlign: 'center', padding: '28px 24px 20px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ color: C.text3, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 6 }}>
          Step {Math.min(displayStep + 1, 5)} of 5
        </div>
        <h1 style={{
          fontSize: 28, fontWeight: 900, color: C.text, letterSpacing: -0.5,
          background: `linear-gradient(135deg, ${C.text}, ${C.text2})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          {STEP_TITLES[displayStep]}
        </h1>
      </div>

      {/* Step content */}
      <div
        ref={contentRef}
        style={{
          flex: 1, padding: '36px 24px 80px', overflowY: 'auto',
          opacity: transitioning ? 0 : 1,
          transform: transitioning ? 'translateY(10px)' : 'translateY(0)',
          transition: 'opacity 0.22s ease, transform 0.22s ease',
        }}
      >
        {displayStep < pipeline.currentStep ? (
          <ReadOnlyStepView
            pipeline={pipeline}
            stepIndex={displayStep}
            onBackToCurrent={() => handleStepClick(Math.min(pipeline.currentStep, 4))}
          />
        ) : (
          <>
            {displayStep === 0 && <Step0Profile pipeline={pipeline} onDone={handleDone} />}
            {displayStep === 1 && <Step1Idea pipeline={pipeline} onDone={handleDone} />}
            {displayStep === 2 && <Step2Reality pipeline={pipeline} onDone={handleDone} />}
            {displayStep === 3 && <Step3Positioning pipeline={pipeline} onDone={handleDone} />}
            {displayStep === 4 && <Step4Validation pipeline={pipeline} onDone={handleDone} />}
          </>
        )}
      </div>

      <style>{`
        @media print {
          #mvp-blueprint ~ * { display: none !important; }
          body { background: #fff !important; color: #000 !important; }
        }
      `}</style>
    </div>
  );
}
